import {
  BRAZIL_TREES_PROJECT_ID,
  INTEGRATELY_DOCUMENTATION_URL,
  LANGUAGES,
  ONTARIO_TREES_PROJECT_ID,
  SCRIPT_TAG_DOCUMENTATION_URL,
  STAMPED_LOYALTY_DOCUMENTATION_URL,
  ZAPIER_DOCUMENTATION_URL,
} from '@/helpers/constants'
import { formatItemsToReadable } from '@/helpers/parsers'

export const en = {
  HomeView: {
    hey: 'Hey, {name}!',
    greeting: 'Welcome back. On this Dashboard you can track the progress of your chosen impact.',
    products_saved: 'Your products have been successfully imported',
  },

  Alert: {
    again: 'Try Again',
    support: 'Contact Support',
  },

  Sidebar: {
    your_impact: 'Your Impact',
    add_impact: 'Add Impact',
    supported_projects: 'Supported Projects',
    shareables: 'Shareables',
    engagement_hub: 'Engagement hub',
    team: 'Your Team',
    network: 'Network',
    impact_wallet: 'Impact Wallet',
    public_ledger: 'Public Ledger',
    support: 'Support',
    account: 'Account',
    analytics: 'Analytics',
    carbon_footprint: 'Carbon Footprint',
    documentation: 'Documentation',
    knowledge_base: 'Knowledge Base',
    contact_support: 'Contact Support',
    whats_new: 'What’s new?',
    open_chat: 'Live Chat',
    contact: 'Contact Us',
    api_docs: 'API Documentation',
    email: 'Email Us',
  },

  Footer: {
    description: 'Join our journey!',
    join: 'Join us',
  },

  MobileMenu: {
    your_impact: 'Your Impact',
    add_impact: 'Add Impact',
    supported_projects: 'Projects',
    shareables: 'Shareables',
    engagement_hub: 'Engagement hub',
    team: 'Your Team',
    public_ledger: 'Ledger',
    support: 'Support',
    account: 'Account',
    network: 'Network',
    impact_wallet: 'Impact wallet',
    analytics: 'Analytics',
    live_page: 'Public profile',
    logout: 'Logout',
    carbon_footprint: 'Carbon Footprint',
    documentation: 'Documentation',
    knowledge_base: 'Knowledge Base',
    contact_support: 'Contact Support',
    whats_new: 'What’s new?',
    open_chat: 'Live Chat',
    contact: 'Contact Us',
    api_docs: 'API Documentation',
    email: 'Email Us',
  },

  Achievements: {
    header: 'Your Achievements',
    months_earth_positive: {
      label: 'Earth Hero',
      flavor: 'months earth positive',
      level_0: '{current} / 3 {flavor}',
      level_1: '{current} / 6 {flavor}',
      level_2: '{current} / 9 {flavor}',
      level_3: '{current} / 12 {flavor}',
      level_4: '{current} / 24 {flavor}',
      level_5: '{current} / 48 {flavor}',
      level_6: '{current} / 72 {flavor}',
      level_7: 'Earth Hero Achieved!',
    },
  },

  AddImpactView: {
    automatic: 'Integrations & automations',
    automatic_text:
      'Create tangible sustainable action on autopilot with our integrations and automations.',
    manual: 'Tailored impact',
    manual_text:
      'Easily create ad hoc impact for product sales, newsletter sign-ups, and other key business touchpoints in just a few clicks.',
    manage: 'Manage',
  },

  IntegrationListView: {
    back: 'Back',
    header: 'Integrations & automations',
    description:
      'Directly integrate with your favourite tools to create automated impact per order, product, newsletter subscriber and more. Connect in seconds, sit back and watch your impact grow.',
    email: 'E-mail',
    feedback: 'Feedback',
    integrator: 'Integrator',
    create_custom_integration_title_custom: 'Create custom integration',
    create_custom_integration_title_zapier: 'Create Zapier integration',
    create_custom_integration_title_integrately: 'Create Integrately integration',
    create_custom_integration_text_custom:
      'With our public API you can connect Greenspark to any other app or fully embed the experience in your product. Learn more by reading our {0}.',
    create_custom_integration_text_zapier:
      'With our Zapier integration API you can connect Greenspark to 5000+ apps. Learn more by reading our {0}.',
    create_custom_integration_text_integrately:
      'With our Integrately integration API you can connect Greenspark to 1100+ apps. Learn more by reading our {0}.',
    create_custom_integration_link_custom: 'API documentation',
    create_custom_integration_link_zapier: 'Zapier integration documentation',
    create_custom_integration_link_integrately: 'Integrately integration documentation',
    create_custom_integration_create_source_text_custom:
      "First let's give your new integration a name. You can also upload an icon to help you more easily identify it later.",
    create_custom_integration_create_source_text_zapier:
      'First let’s give your new integration a name. This is only used internally and will never be seen by your customers.',
    create_custom_integration_create_source_text_integrately:
      'First let’s give your new integration a name. This is only used internally and will never be seen by your customers.',
    create_custom_integration_create_source_description_custom:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Custom Integration. For example: you can store the connected application name as a source name.',
    create_custom_integration_create_source_description_zapier:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Zapier integration.',
    create_custom_integration_create_source_description_integrately:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Integrately integration.',
    create_custom_integration_create_trigger_text_custom:
      'Great! now we can set up your first trigger. A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Use triggers to easily manage all of your API calls and ensure they are classified correctly in your analytics. Don’t worry, you can always add more later.',
    create_custom_integration_create_trigger_text_zapier:
      'We can set up your first trigger. A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Use triggers to easily manage all of your Zapier calls and ensure they are classified correctly in your analytics. Don’t worry, you can always add more later.',
    create_custom_integration_create_trigger_text_integrately:
      'We can set up your first trigger. A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Use triggers to easily manage all of your Integrately calls and ensure they are classified correctly in your analytics. Don’t worry, you can always add more later.',
    create_custom_integration_source_name: 'Source name',
    crate_custom_integration_upload_icon: 'Upload icon',
    create_custom_integration_trigger_name: 'Trigger name',
    create_custom_integration_trigger_type: 'Trigger type',
    create_custom_integration_create_apikey_text_custom:
      'Next let’s create an API key that you can use to access with the Greenspark API. Learn more with our {0}.',
    create_custom_integration_create_apikey_text_zapier:
      'Now that you have a source and trigger to identify your Zapier calls, let’s create a Zapier key to authenticate your calls.',
    create_custom_integration_create_apikey_text_integrately:
      'Now that you have a source and trigger to identify your Integrately calls, let’s create an Integrately key to authenticate your calls.',
    source_name: 'Source name',
    upload_icon: 'Upload icon',
    trigger_name: 'Trigger name',
    trigger_type: 'Trigger type',
    key_name: 'Key name',
    your_key_title_custom: 'Your API Key',
    your_key_title_zapier: 'Your Zapier Key',
    your_key_title_integrately: 'Your Integrately Key',
    your_key_text_custom:
      'Copy you API key and use it to authenticate any calls you make. You can manage all of your keys from within your Greenspark Account.',
    your_key_text_zapier:
      'Copy you Zapier key and use it to authenticate any Zapier calls you make. You can manage all of your keys from within your Greenspark Account.',
    your_key_text_integrately:
      'Copy you Integrately key and use it to authenticate any Integrately calls you make. You can manage all of your keys from within your Greenspark Account.',
    your_key_note_custom: 'Please note you can only see this API key once so make sure to save it.',
    your_key_note_zapier:
      'Please note you can only see this Zapier key once so make sure to save it.',
    your_key_note_integrately:
      'Please note you can only see this Integrately key once so make sure to save it.',
  },

  EtsyIntegrationAlert: {
    title: 'Integrate with Etsy',
    description:
      "The term 'Etsy' is a trademark of Etsy, Inc. This application uses the Etsy API but is not endorsed or certified by Etsy, Inc.",
    continue: 'Continue',
    cancel: 'Cancel',
  },

  IntegrationListSources: {
    help: 'Need help finding your credentials?',
    description_email: 'Choose from our email integrations to automatically add impact:',
    per_subscriber: 'Per new subscriber',
    description_review: 'Choose from our feedback integrations to automatically add impact:',
    per_product_review: 'Per product review',
    per_company_review: 'Per company review',
  },

  CustomIntegrationList: {
    title: 'Custom integrations',
    description:
      'Connect your own SaaS platform, website or app. Use our API to connect with anything. Learn more with our {0}.',
    link: 'API documentation',
  },

  ManageStoreView: {
    title: 'Manage {storeType} Automations',
    description:
      'Choose the triggers that allow your customers to create positive impact. Stack impacts to create unique combinations that maximise customer spend and engagement. Or for one-off campaigns for special categories, product launches and more.',
    back: 'Back',
    error: 'An error occurred, please try again or get in touch and we would be happy to help.',
    saved: 'Settings saved!',
    success:
      'The payment has been added to your next invoice and the impact will be added to your dashboard immediately. Thanks for your support!',
    waiting:
      'The products of your store are being set up. Please be patient, this could take a few minutes',
  },

  ManageSourceView: {
    title: 'Manage {sourceType} integration',
    description:
      'Choose the triggers that allow your customers to create positive impact. Stack impacts to create unique combinations that maximise customer spend and engagement. Or for one-off campaigns for special categories, product launches and more.',
    back: 'Back',
    error: 'An error occurred, please try again or get in touch and we would be happy to help.',
    saved: 'Settings saved!',
  },

  ManageCustomIntegrationView: {
    description_custom:
      'Easily manage all of your triggers for your {0} integration. You can always learn more in our {1}.',
    description_zapier: 'Easily manage all of your triggers for your {0} integration.',
    description_integrately: 'Easily manage all of your triggers for your {0} integration.',
    link: 'API documentation',
    name: 'Name',
    type: 'Type',
    trigger_id: 'Trigger Id',
    created: 'Created',
    triggers: 'Triggers',
    triggers_description_custom:
      'A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Use triggers to easily manage all of your API calls and ensure they are classified correctly in your analytics.',
    triggers_description_zapier:
      'A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Use triggers to easily manage all of your Zapier calls and ensure they are classified correctly in your analytics.',
    triggers_description_integrately:
      'A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Use triggers to easily manage all of your Integrately calls and ensure they are classified correctly in your analytics.',
    create_trigger: 'Create trigger',
    source: 'Source',
    source_id: 'Source Id',
    source_description_custom:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Custom Integration. For example: you can store the connected application name as a source name.',
    source_description_zapier:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Zapier integration.',
    source_description_integrately:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Integrately integration.',
    source_description_help: 'Learn more with our {0}.',
    source_description_help_link: 'API documentation',
    create_trigger_title: 'Create trigger',
    trigger_name: 'Trigger name',
    trigger_type: 'Trigger type',
    edit_trigger_title: 'Edit trigger',
    delete_trigger_title: 'Delete trigger',
    delete_trigger_text:
      'By deleting the {0} Trigger, you will not be able to use it in your code anymore and it will be lost for ever. Any processes that use this trigger should be updated.',
    delete_trigger_checkbox: 'I want to delete this Trigger',
    delete_source_title: 'Delete source',
    delete_source_text:
      'By deleting the {0} Source, you are removing the integration and it will be lost for ever. Any processes that use this source should be updated.',
    delete_source_checkbox: 'I want to delete this Source',
    edit_source_title: 'Edit source',
    source_name: 'Source name',
    upload_icon: 'Upload icon',
    edit_icon: 'Edit icon',
    notify_delete_trigger: 'Trigger {trigger} was successfully deleted',
    notify_delete_source: 'Source {source} was successfully deleted',
  },

  OffsetPerSubscriber: {
    per_subscriber: 'Impact per new email subscriber',
    per_subscriber_header: 'New email subscriber impact',
    per_subscriber_description:
      'Automatically add impact for every new newsletter subscriber. Simply choose your impact and the amount per subscriber. The totals are automatically recorded in your dashboard whenever a new subscriber is created.',
    per_sms_subscriber: 'Impact per new SMS subscriber',
    per_sms_subscriber_header: 'New SMS subscriber impact',
    per_sms_subscriber_description:
      'Automatically add impact for every new SMS subscriber. Simply choose your impact and the amount per subscriber. The totals are automatically recorded in your dashboard whenever a new SMS subscriber is created.',
  },

  OffsetPerReview: {
    impact_per_product_review: 'Impact on a product review',
    impact_per_product_review_header: 'New product review impact',
    impact_per_product_review_description:
      'Automatically add impact for every new product review. Simply choose your impact and the amount per review. The totals are automatically recorded in your dashboard whenever a new review is created.',
    impact_per_company_review: 'Impact on a company review',
    impact_per_company_review_header: 'New company review impact',
    impact_per_company_review_description:
      'Automatically add impact for every new company review. Simply choose your impact and the amount per review. The totals are automatically recorded in your dashboard whenever a new review is created.',
  },

  ActiveIntegration: {
    disconnect_store: 'Manage Active {storeType} Stores',
    disconnect_text1: 'Connect / disconnect all of your sales channels.',
    manage: 'Manage',
    integration_active: 'Add or delete your sales channels.',
    contact: ' Get in touch.',
    multiple: 'Any questions?',
    disconnect: 'Disconnect',
    disconnect_header: 'Danger',
    disconnect_text:
      'You are about to disconnect your sales channel which will automatically cancel all configured automations. Are you sure you want to proceed and remove {name}?',
    disconnect_text_shopify:
      'As you have come through Shopify, removing the integration will also delete the app within Shopify and cancel your account with us. If you would like to change sales channel and not cancel your account then',
    cancel_error:
      'There was an error disconnecting your store. Please try again or get in touch and we will be happy to help.',
    cancel: 'Cancel',
    connect: 'Add Store',
    add: 'Add Sales Channel Integration',
  },

  StoreTile: {
    active: 'Active',
    coming_soon: 'Coming Soon',
  },

  SourceTile: {
    active: 'Active',
  },

  CreateSourceTile: {
    text: 'Create custom integration',
  },

  ActiveSource: {
    disconnect_source: 'Manage Active Service',
    disconnect_text1: 'Disconnect your service.',
    manage: 'Manage',
    integration_active: 'Delete your connected service.',
    contact: ' Get in touch.',
    multiple: 'Any questions?',
    disconnect: 'Disconnect service',
    disconnect_header: 'Danger',
    disconnect_text:
      'You are about to disconnect your service which will automatically cancel all configured automations. Are you sure you want to proceed?',
    cancel_error:
      'There was an error disconnecting your service. Please try again or get in touch and we will be happy to help.',
    cancel: 'Cancel',
  },

  ManualOffsets: {
    back: 'Back',
    error:
      'An error occurred with your payment. Please try again or contact us and we would be happy to help.',
    again: 'Try Again',
    support: 'Contact Support',
    success:
      'The payment has been added to your next invoice and the impact will be added to your dashboard immediately. Thanks for your support!',
    upgrade: 'Upgrade',
    upgrade_text:
      'Upgrade to a paid account to offset your monthly carbon and plastic footprints. Easily track everything right here in your Greenspark dashboard and share your impact with the world!',
  },

  HowItWorks: {
    header: 'Increase Your Impact',
    text: 'Greenspark superheroes go further! Offset per unit, order or customer.',
    text_personal:
      'Greenspark superheroes go further! Add offset additional plastic or plant more trees, mother earth thanks you.',
    integrations: 'Looking for direct integrations?',
    click_here: 'Click here',
    how_it_works: 'How It Works',
    choose_impact: 'Choose your impact',
    decide: 'Decide how much you want to offset',
    decide_personal: 'Decide how much you want to offset',
    impact_added: 'Your impact is automatically added to your Greenspark profile',
  },

  AddCart: {
    confirm: 'Confirm',
    min: 'Add Minimum',
    minimum: 'Min spend',
  },

  AccountView: {
    profile: 'Update Profile',
    users: 'Users',
    billing: 'Billing',
    api_key: 'API',
  },

  ApiKey: {
    title: 'API Keys',
    description_custom_integrations:
      'Use your API keys to query the Greenspark API and Widget API.',
    copy: 'copy',
    is_copied: 'copied!',
    copy_title: 'Copy to clipboard',
    generate: 'create api key',
    placeholder: 'API Key',
    help_text: 'Learn more with our {0}',
    help_link: 'API documentation.',
    alert_text: 'Key copied!',
    table_name: 'Name',
    table_type: 'Type',
    table_created: 'Created',
    create_key_name: 'Key name',
    create_key_title: 'Create API Key',
    create_key_text: 'Create a new API key that you can use with the Greenspark API or Widget API.',
    your_key_title: 'Your API Key',
    your_key_text_1: 'Copy your API key and use it to authenticate any calls you make.',
    your_key_text_2: 'Please note you can only see this API key once so make sure to save it.',
    close: 'Close',
    delete_key_title: 'Delete API Key',
    delete_key_text:
      'By deleting you API key it will be lost for ever. Any processes which use your API key should be updated.',
    delete_key_checkbox: 'I want to delete this API key.',
    edit_key_title: 'Edit API Key',
    save: 'Save',
    upgrade_text: 'In order to use this feature, please upgrade your plan.',
    upgrade: 'upgrade',
  },

  ApiUsage: {
    usage: 'Usage',
    description:
      'Keep an overview of usage and limits within your Greenspark account.Please note that failed API requests are never billed.',
    usage_period: 'Usage period',
    widgets: 'Widget API Requests',
    impacts: 'Impact API',
    projects: 'Projects API',
    estimations: 'Estimations API',
    reports: 'Reports API',
    email: 'Emails API',
    account: 'Account Management API',
    overall: 'Greenspark API Requests',
    increase: 'Looking to increase your plan limits?',
    contact_us: 'Contact us',
    help_text_limit: '({limit} included, additional requests will be billed at {chargeFee})',
    help_text_no_limit: '(Requests will be billed at {chargeFee})',
    unlimited: 'Unlimited',
  },

  UpdateProfile: {
    upload: 'Upload Logo',
    upload_picture: 'Upload Picture',
    first_name: 'First Name',
    last_name: 'Last Name',
    company_name: 'Company / Brand Name',
    email: 'Email Address',
    profile_description: 'Profile Description',
    website: 'Website',
    website_text: 'Displayed on your public page',
    profile_description_placeholder:
      'Why did you join Greenspark and start fighting the climate madness?',
    save: 'Save',
  },

  Billing: {
    your_invoices: 'Billing History',
    no_invoices: 'You do not have an active subscription.',
    shopify:
      'Since you have an active Shopify integration, your billing can be seen in your Shopify bill.',
    update_payment: 'Update Billing Details',
    manage_plan: 'Manage your plan',
    manage: 'Manage',
    trial_disclaimer:
      'You’re in a free trial. If you decide to upgrade or downgrade, your free trial will end, and you’ll be charged.',
    update: 'Update',
    cancel: 'Cancel',
    cancel_subscriptions: 'Cancel Your Subscription',
  },

  CancelSubscriptionDialog: {
    cancel_header: 'Cancel Your Subscription',
    cancel_text: 'You are about to cancel your subscription and lose access to your dashboard.',
    cancel_text1:
      'If you decide to go ahead with the cancellation, then we would love to understand how we can do better.',
    cancel_error:
      'There was an error cancelling your subscription. Please try again or get in touch with us and we would be happy to help.',
    cancel_subscription: 'Cancel Subscription',
    back: 'Back to Dashboard',
    email_us:
      "If you want to cancel your subscription, please reach out to our team at <a class='cancel_email' href='mailto:support{'@'}getgreenspark.com'>support{'@'}getgreenspark.com</a> and we will get back to you and find a resolution within 1 business day.",
  },

  ProjectRefactorLogoutDialog: {
    header: 'Heads Up! New projects available.',
    sub_header:
      "<p><strong>We're making some exciting changes to improve your experience!</strong> To ensure a smooth transition, you'll <strong>need to log out.</strong> Don't worry, it'll only take a moment!</p>",
    expect: 'What to expect:',
    list_header: '<strong>New project options</strong> - Dive into new ways to make a difference!',
    list_1: '<strong>Plant trees in</strong> the majestic forests of <strong>Oregon.</strong>',
    list_2: 'Support vital <strong>tree restoration efforts in Kenya.</strong>',
    list_3: 'Help protect our oceans with <strong>kelp planting initiatives.</strong>',
    cta_description:
      'Just click "Log me out!" below, you\'ll be logged out briefly. Then, log back in to explore the exciting updates!',
    log_out: 'Log me out!',
  },

  Invoice: {
    download: 'Download',
  },

  LoginView: {
    login: 'Login',
    email: 'Email address',
    password: 'Password',
    forgot_password: 'Reset my password',
    login_error: 'Your email address and password do not match.',
    not_member: 'Not a member yet?',
    signup: 'Signup',
  },

  ForgotPasswordView: {
    forgot_password: 'Reset Password',
    email: 'Email Address',
    email_sent: 'We have sent a password reset link to your email.',
    error: 'There was an error resetting your password. Please try again or contact us.',
    login: 'Back to Login',
  },

  ResetPasswordView: {
    forgot_password: 'Update Password',
    password: 'Password',
    updated: 'Password Updated',
    error:
      'There was an error resetting your password. Please try again or request a new reset link:',
    password_hint:
      'Your Password must be at least 6 letters long, contain at least one special character and a mix of upper/lower case letters.',
    login: 'Back to Login',
  },

  PreSignupView: {
    choose: 'Choose Account Type',
    cancel: 'Back to Login',
    business: 'Business',
    personal: 'Personal',
    family: 'Family',
  },

  SignupView: {
    password: 'Password',
    email: 'Email',
    signup: 'Signup',
    already_member: 'Already a member?',
    login: 'Login',
  },

  Signup: {
    name: 'Name',
    first_name: 'First Name*',
    last_name: 'Last Name*',
    password: 'Password*',
    email: 'Email*',
    vat_id: 'VAT Number (EU only)',
    signup: 'Signup',
    already_member: 'Already a member?',
    login: 'Login',
    company_name: 'Company Name*',
    create_account: 'Start being a force of good',
    text: 'Enter your email address and password to create your account.',
    continue_to_payment: 'Continue to Payment',
    create: 'Create account',
    continue: 'Continue',
    back: 'Back',
    password_hint:
      'Your Password must be at least 6 letters long, contain at least one special character and a mix of upper/lower case letters.',
    already_used: 'Email already used.',
    already_account:
      'Do you already have an account with us? If so, please login and we will add your new Shopify store to your existing account.',
    ask_to_login: 'Do you already have an account with us? If so, please login.',
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    accept: 'Accept our {0} & {1}',
  },

  SignupEcommerce: {
    name: 'Name',
    first_name: 'First Name*',
    last_name: 'Last Name*',
    password: 'Password*',
    email: 'Email*',
    vat_id: 'VAT Number (EU only)',
    signup: 'Signup',
    already_member: 'Already a member?',
    login: 'Login',
    company_name: 'Company Name*',
    create_account: 'Start being a force of good',
    create_account_invite: 'Create your Greenspark account',
    text: 'Enter your email address and password to create your account.',
    continue_to_payment: 'Continue to Payment',
    create: 'Create account',
    continue: 'Continue',
    back: 'Back',
    password_hint:
      'Your Password must be at least 6 letters long, contain at least one special character and a mix of upper/lower case letters.',
    already_used: 'Email already used.',
    already_account:
      'Do you already have an account with us? If so, please login and we will add your new Shopify store to your existing account.',
    ask_to_login: 'Do you already have an account with us? If so, please login.',
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    accept: 'Accept our {0} & {1}',
    invalid_invite_url:
      'Your signup invite url is invalid. Please get in touch with the account owner.',
    invite_is_deleted:
      'Your invitation has been revoked. Please get in touch with the account owner.',
    invite_not_found: 'Your invitation is not found. Please get in touch with the account owner.',
    invite_is_already_accepted:
      'Your invitation has already been accepted. Please get in touch with the account owner.',
    invite_email_mismatch:
      'The email is not matching with the invitation email. Please get in touch with the account owner.',
    something_went_wrong: 'Something went wrong. Please try again.',
  },

  ChooseEmployees: {
    employees: 'Add Your Team',
    amount: 'Team Members',
    continue: 'Continue',
    skip: 'Continue without team',
    standard: 'Standard',
    superior: 'Superior',
    premium: 'Premium',
    per_employee: 'Per employee / month',
    plastic: '100 plastic bottles',
    removed_month: 'removed each month',
    removed_year: 'removed each year',
    mads_footprint: 'Greenspark Footprint',
    info: '1 Greenspark Footprint = 17 tonnes of CO2',
    total_employees: 'Total Team Members:',
    total_due: 'Total Per Month',
    month: 'Month',
    co2: 'Tonnes CO2 / Month',
    cancel: 'Back',
  },

  ChoosePersonalPlan: {
    subscription: 'Choose Your Subscription Plan',
    amount: 'Amount',
    continue: 'Continue',
    personal: 'Personal',
    personal_description: 'The perfect plan for the average European citizen!',
    plastic_hero_description: 'Go carbon & plastic positive!',
    earth_hero_description: 'Ideal if you are a frequent flyer or want to boost your impact.',
    plastic_hero: 'Plastic Hero',
    earth_hero: 'Earth Hero',
    per_month: 'Per month',
    plastic: '100 plastic bottles',
    plastic_plus: '350 plastic bottles',
    removed_month: 'removed each month',
    removed_year: 'removed each year',
    mads_footprint: 'Greenspark Footprint',
    info: '1 Greenspark Footprint = 1.42 tonnes of CO2',
    total_employees: 'Total Team Members:',
    total_due: 'Total Per Month',
    month: 'Month',
    co2: 'Tonnes CO2 / Month',
    cancel: 'Back',
  },

  ChooseFamilyPlan: {
    subscription: 'Choose Your Subscription Plan',
    amount_adult: 'Adults',
    amount_child: 'Children',
    amount_pet: 'Pets',
    continue: 'Continue',
    positive_family: 'Positive Family',
    earth_positive_family: 'Earth Positive Family',
    per_adult_month: 'Per adult / month',
    per_child_month: 'Per child / month',
    per_pet_month: 'Per pet / month',
    cancel: 'Back to Login',
  },

  AddOns: {
    back: 'Back',
    header: 'Plan Add-ons',
    plastic_hero: 'Plastic Hero',
    plastic_hero_tooltip:
      'The global yearly average plastic use per capita is 65.7kg. In the Plastic Hero upgrade we have added 25% in order to ensure your team goes plastic positive!',
    plastic_hero_description:
      'Go Plastic Positive by offsetting an additional 250 plastic bottles per team member / month.',
    team_members: 'Add Team Members',
    tree_planter: 'Tree Planter',
    tree_planter_tooltip:
      'Planting trees is one of the best climate solutions available today. An increase in forested areas by 25%, could erase nearly 100 years of emissions!',
    tree_planter_description:
      'Take the extra step by planting trees for your team each month and grow your company forest.',
    add_trees: 'Add Trees',
    continue: 'Continue',
    error: 'Plastic Hero add-on can not exceed team members.',
  },

  AddOnsPersonal: {
    back: 'Back',
    header: 'Plan Add-ons',
    plastic_addon: 'Plastic Upgrade',
    plastic_addon_tooltip:
      'The global yearly average plastic use per capita is 65.7kg. In the Plastic Upgrade we have added 25% in order to ensure you go plastic positive!',
    plastic_addon_description: '1 x Additional Plastic Footprint (350 bottles) reduced per month.',
    add_members: 'Add Members',
    add_plastic: 'Add Plastic',
    tree_addon: 'Tree Upgrade',
    tree_addon_tooltip:
      'Planting trees is one of the best climate solutions available today. An increase in forested areas by 25%, could erase nearly 100 years of emissions!',
    tree_addon_description:
      'Take the extra step by planting trees per month and grow you own forest.',
    add_trees: 'Add Trees',
    continue: 'Continue',
    error: 'Maximum 1 x Plastic Upgrade per member.',
  },

  UserInfo: {
    header: 'Your Information',
    company_name: 'Company*',
    first_name: 'First Name*',
    last_name: 'Last Name*',
    continue: 'Continue',
    back: 'Back',
    country: 'Country*',
    vat_id: 'VAT Number',
    vat_id_required: 'VAT Number*',
    street: 'Street*',
    postal_code: 'Post/Zip Code*',
    city: 'City*',
  },

  TotalsPersonal: {
    carbon: 'Tonne of CO2 | Tonnes of CO2',
    plastic: 'Plastic Bottle | Plastic Bottles',
    trees: 'Tree | Trees',
    member: 'Member | Members',
    total: 'Total Monthly Plan',
  },

  TotalsFamily: {
    carbon: 'Tonne of CO2 | Tonnes of CO2',
    plastic: 'Plastic Bottle | Plastic Bottles',
    trees: 'Tree | Trees',
    tree: 'Tree',
    members: 'Family Members',
    member: 'Family Member',
    total: 'Total Monthly Plan',
  },

  CheckoutSuccessView: {
    thanks: 'Thanks For Signing Up!',
    text1:
      'Thanks for joining our community of change-making businesses working to create a better future for the planet!',
    text2: 'We can not wait to see your Earth Positive Impact grow!',
    text_personal1: 'You have just become the latest member of the Greenspark Family!',
    text_personal2:
      'From today onwards you are part of a community of like minded people working to offset their carbon footprint, reduce the amount of plastic in the ocean, and all round make the planet a better place!',
    text_personal3: 'Thanks for being an Earth Champion!',
    success: 'Sign up successful!',
    success_sub:
      'You’re in! Now it’s time to add your impact. Start by connecting your online business, or adding impact for your team.',
    add_integration: 'Add an integration',
    add_integration_text: 'Automatically offset activity in your online business in a few clicks.',
    add_team: 'Add your team',
    add_team_text: 'Offset the carbon impact of your team & business activity.',
    continue: 'Continue',
    continue_dashboard: 'Continue to Dashboard',
  },

  Shareables: {
    manage: 'Manage',
    badges: 'Badges & Widgets',
    badges_text:
      'Add badges and widgets that automatically link to your public profile, update in real time, and display your impact to your customers.',
    qr: 'QR code',
    qr_text:
      'Use your unique QR code on swing tags, flyers or in-store merchandising to direct your users to your public profile.',
    qr_text_personal:
      'Use your unique QR code on business cards or your personal website to easily share your public profile',
    engagement: 'Post purchase engagement',
    engagement_text:
      'Share certificates with your customers and allow them to track and share their impact. Engage with your customers through impact.',
  },

  BadgesMain: {
    full_width_banner_header: 'Full-width banner',
    static:
      'This badge can be placed on your own website to display that you are an Earth Positive business. It automatically links through to your public profile. You can place it in your website footer or anywhere you want to display it on the page by copying the code snippet below and pasting it into the relevant section of your site. If you are having any difficulty adding the code to your site feel free to reach out to our support team.',
    dynamic:
      'This badge can be placed on your own website to display your Earth Positive impact. It updates automatically as your impact grows. You can place it in your website footer or anywhere you want to display it on the page by copying the code snippet below and pasting it into the relevant section of your site. If you are having any difficulty adding the code to your site feel free to reach out to our support team.',
    dynamic_header: 'Dynamic widgets',
    static_header: 'Static badges',
    round_header: 'Round badges',
    all: 'All',
    badges: 'Badges',
    banners: 'Banners',
    widgets: 'Widgets',
    use_widget: 'Use widget',
    back: 'Back',
    offsetPerOrderSectionTitle: 'Per order widgets',
    offsetPerProductSectionTitle: 'Per product widgets',
    offsetBySpendSectionTitle: 'Spend threshold widgets',
    offsetByStoreRevenueSectionTitle: 'Tiered spend level widgets',
    offsetByPercentageOrderSectionTitle: '% of order widgets',
    byPercentageOfRevenueSectionTitle: '% of revenue widgets',
    orderImpactsSectionTitle: 'Cart based widgets',
    shopify_two: 'Shopify Blocks',
    shopify_script_tag: 'Shopify Script Tag',
    widget_api: 'Widget API',
    rebuy_smart_cart: 'Rebuy Smart Cart',
    code_snippet: 'Code Snippet',
  },

  LivePage: {
    header: 'Your Public Profile',
    info_text:
      'Embed it anywhere, show it off everywhere. Share your public profile with the world!',
    view: 'View Page',
    edit: 'Edit Page',
    copy_link: 'Copy Link',
    copied_link: 'Copied!',
  },

  HowItWorkShare: {
    header: 'Earth Positive Widgets',
    text: 'Showcase your impact to your customers through static and dynamic badges, widgets, and banners. Clearly and transparently communicate your environmental action and inspire your community with your unique impact story.',
    multilanguage: `<strong>Multi-language support:</strong> Automatically displayed in <strong>${formatItemsToReadable(
      (LANGUAGES || []).map((language) => language?.title),
    )}</strong> based on <strong>geolocation.</strong>`,
    learn_more:
      '<strong>Want to learn more? </strong>Explore how this feature works in our <a href="https://help.getgreenspark.com/en/article/which-languages-do-you-translate-too-rorasf/" target="_blank"> knowledge base.</a>',
  },

  Badge: {
    app_block: 'App block',
    app_embed: 'App embed',
    use: 'Use Badge',
    use_badge: 'Use Badge',
    use_widget: 'Use Widget',
    use_banner: 'Use Banner',
    copy: 'Copy Code',
    copy1: 'Copy',
    copied: 'Copied!',
    send: 'Send Code',
    overlay_shopify:
      'Place this badge in your Shopify store footer. It automatically updates and links to your Greenspark Public Profile. Our badges are compatible with all Shopify themes.',
    overlay_shopify_block:
      'Place this badge anywhere in your Shopify store. It automatically updates and links to your Greenspark Public Profile.',
    overlay_shopify_banner:
      'Place this banner anywhere in your Shopify store and customise the impact you display. It automatically updates and links to your Greenspark Public Profile.',
    overlay_api_banner: 'Use the Widget API to display this banner anywhere!',
    overlay_shopify_product:
      'Place this badge on your product pages. It automatically updates depending on your per product impact and only shows on products where impact is active.',
    overlay_api: 'Use the Widget API to display this badge anywhere!',
    how_it_works: 'How it works',
    one: 'Choose your store in the list below',
    two: 'Click on the Proceed to Shopify button',
    three: 'Find the Greenspark Badge app and click on the arrow button to expand the options',
    four: 'Copy your Greenspark Id & Badge Url below and paste them in the provided spaces in Shopify',
    five: 'Set your background colour, horizontal alignment & badge size in Shopify',
    six: 'Toggle the switch in Shopify to activate your badge',
    id: 'Greenspark Id',
    badge_url: 'Badge Url',
    proceed_to_shopify: 'Proceed to Shopify',
    go_to_shopify: 'Go to Shopify',
    go_to_rebuy: 'Go to Rebuy',
    go_copy: 'Looking to add the code directly into your platform?',
    go_shopify: 'Looking for Shopify?',
    questions: 'Any questions?',
    chat_with_us: 'Chat with us!',
    help: '{0} or {1}',
    help_read: 'Read the docs',
    help_chat: 'chat with us!',
    shopify_tutorial: {
      step_1: 'From your Shopify admin, go to Online Store > Themes.',
      step_2: 'Find the theme that you want to edit, and then click Customize.',
      step_3: 'Navigate to the page where you want to add the app section. Click Add section.',
      step_4:
        'From the drop-down menu, in the Apps section, select the Greenspark badge that you want to add to the page.',
      step_5:
        'Optional: move the app section to the place you want it on the page, and customize the block using any available settings.',
      step_6: 'Click Save.',
    },
    shopify_script_tag_tutorial: {
      title: 'Add this Widget to your Shopify Cart Page or Drawer using Script Tags',
      description: `Easily integrate this widget into the cart page or drawer of your Shopify store without needing to use our API. Just follow the simple steps below, and if you have any questions, check out our <a href="${SCRIPT_TAG_DOCUMENTATION_URL}" target="_blank">Guide for using Script Tags in Shopify.</a>`,
      step_1:
        '<strong>Choose your Widget action</strong>: Decide whether your widget should <strong>open a pop-up</strong> when clicked or remain static.',
      banner_step_1:
        '<strong>Choose your Widget display</strong>: Configure the display of the widget.',
      step_2:
        '<strong>Copy the  &lt;div&gt; Tag:</strong> This will determine where the widget will appear on your page. Click on the icon to copy the <strong>&lt;div&gt;</strong> Tag field.',
      step_3:
        'Go to your <strong>Shopify Admin > Online Store > Themes.</strong> Within <strong>Actions > Edit Code</strong>, locate the file where you want your widget to appear. Paste the <strong>&lt;div&gt;</strong> tag where you want the widget to be positioned in the code.',
      step_4:
        '<strong>Copy the HTML Snippet:</strong> This is the code that loads the widgets to display within the &lt;div&gt; above. Click on copy. Then, in Shopify, <strong>paste the HTML Snippet into the header of your website</strong>, just before the opening &lt;body&gt; tag. This ensures the script loads properly.',
      step_5:
        '<strong>Save</strong> your changes and <strong>preview your store</strong> to ensure the widget displays correctly.',
      script_tag_label: 'Your HTML Snippet (script tag)',
      div_tag_label: 'This is your widget &lt;div&gt; tag',
      deactivate: '<strong>Activate widget</strong>',
      deactivate_description:
        'If you deactivate this toggle, the widget will be hidden across all pages where it’s installed.',
      activate_popup: 'Activate pop-up on click',
      deactivate_popup: 'Do not activate pop-up',
      light: 'Use light mode pop-up',
      dark: 'Use dark mode pop-up',
      only_once: 'Only add this script tag to your website header once.',
      banner_options: 'The icons to show on the banner.',
      banner_image_url: 'The URL of the image to be displayed on the banner (optional).',
      banner_title: 'The title of the banner (optional).',
      banner_description: 'The description of the banner (optional).',
      banner_call_to_action: 'The URL to redirect to when the button is clicked (optional).',
      banner_text_color: 'The color of the text in hex format (optional).',
      banner_button_background_color: 'The color of the button in hex format (optional).',
      banner_button_text_color: 'The color of the button text in hex format (optional).',
      earth_positive: 'Months Earth Positive',
    },
    api_tutorial: {
      step_1: 'Create your Widget API key by heading to your {0}',
      step_2: 'Familiarise yourself with the {0}',
      step_3: 'Implement widgets and display your impact!',
    },
    rebuy_tutorial: {
      title: 'Setting up your Rebuy Smart Cart',
      step_1: 'First, you need to create a widget API key.',
      click_here: 'Click here',
      step_2: 'Select the store you are using with.',
      step_3: 'Copy the key displayed below.',
      step_4: 'Follow the',
      installation_guide: 'Rebuy + Greenspark Smart Cart App Installation Guide',
      key_input_label: 'Here’s your Rebuy API key for this store:',
      key_input_placeholder: 'To see your Rebuy API key here, you need to set up a Widget API key.',
    },
    copy_action_success_feedback: 'Copied!',
    copy_action_error_feedback: 'Failed to copy!',
    account_link: 'Account -> API Keys',
    documentation_link: 'API documentation',
    help_more: 'Still have questions?',
    shopify_switch: 'Looking for Shopify?',
    api_switch: 'Looking for Widget API?',
    compatible_platforms: 'Add Widget',
    shopify: 'Shopify Blocks',
    shopify_script_tag: 'Shopify Script Tag',
    widget_api: 'Widget API',
    rebuy_smart_cart: 'Rebuy Smart Cart',
    code_snippet: 'Code Snippet',
    code_snippet_tutorial: {
      title: 'Add this Widget using a Code Snippet',
      description:
        'Easily add this widget by copy and pasting the code snippet below. This can be used anywhere where you can add HTML, for example on your website, store or in emails.',
      copy: 'Copy',
      copied: 'Copied to clipboard',
    },
  },

  WidgetDialog: {
    use: 'Use Badge',
    overlay_shopify:
      'Place this badge in your Shopify store footer. It automatically updates and links to your Greenspark Public Profile. Our badges are compatible with all Shopify themes.',
    how_it_works: 'How it works',
    one: 'Choose your store in the list below',
    two: 'Click on the Proceed to Shopify button',
    three: 'Find the Greenspark Badge app and click on the arrow button to expand the options',
    four: 'Copy your Greenspark Id & Badge Url below and paste them in the provided spaces in Shopify',
    five: 'Set your background colour, horizontal alignment & badge size in Shopify',
    six: 'Toggle the switch in Shopify to activate your badge',
    id: 'Greenspark Id',
    badge_url: 'Badge Url',
    proceed_to_shopify: 'Go to Shopify',
    go_copy: 'Looking to add the code directly into your platform?',
    go_shopify: 'Looking for Shopify?',
    support: 'Any questions?',
    support1: 'Chat with us!',
    tutorial_step_1: 'From your Shopify admin, go to Online Store > Themes.',
    tutorial_step_2: 'Find the theme that you want to edit, and then click Customize.',
    tutorial_step_3:
      'Navigate to the page where you want to add the app section. Click Add section.',
    tutorial_step_4:
      'From the drop-down menu, in the Apps section, select the Greenspark badge that you want to add to the page.',
    tutorial_step_5:
      'Optional: move the app section to the place you want it on the page, and customize the block using any available settings.',
    tutorial_step_6: 'Click Save.',
  },

  QrCode: {
    back: 'Back',
    how_it_works: 'QR code',
    how_it_works1:
      'Use your unique QR code on swing tags, flyers or in-store merchandising to direct your users to your public profile.',
    how_it_works1_personal:
      'Use your unique QR code on business cards or your personal website to easily share your public profile',
    customise: 'Customise your QR code',
    download: 'Download your QR code',
    done: 'Use it on your swing-tags, flyers, in-store marketing campaigns and more',
    done_personal: 'Use it on your business cards, personal website and more',
    analytics: 'Analytics',
  },

  HowItWorksUtility: {
    how_it_works: 'How it Works',
  },

  FunkyBackground: {
    background: 'Background colour',
    color: 'Code colour',
    download: 'Download',
    size: 'Size',
    scan: 'Scan to see our impact with',
    hide_frame: 'Show frame',
    custom_text: 'Custom text',
    file_format: 'File format',
    disclaimer: 'This operation may take up to 5 minutes to complete.',
    error_message:
      'Something went wrong while downloading your QR Code. Please try again or contact us.',
  },

  LivePageView: {
    impact: 'Impact',
    our_impact: 'Our Impact',
    my_impact: 'My Impact',
    projects: 'Supported Projects',
    public_ledger: 'Public Ledger',
    back: 'Back to Dashboard',
    logout: 'Logout',
    update: 'Update Page',
    home: 'Home',
    about: 'About',
    projects_home: 'Projects',
  },

  TopStatsPublic: {
    earth_positive: 'Months Earth Positive',
    header: 'Our Impact',
  },

  Banner: {
    description:
      'We joined Greenspark to ensure that {accountName} has a positive impact on our planet and its people. Check out our impact so far and join our journey!',
    description_personal:
      'I joined Greenspark to increase my positive impact on our planet and its people. Check out my impact so far and join my journey!',
    our_website: 'Our website',
  },

  ComparisonCardPublic: {
    hectare: 'hectare',
    billion: 'bn',
    million: 'mn',
  },

  TopStatCard: {
    transferredText:
      '{amount} have been transferred from a previous source. Check the public ledger to see the proof.',
  },

  ProjectCard: {
    learn: 'Learn more',
  },

  LivePublicLedger: {
    header: 'Public Ledger',
    text: "Every quarter we provide all of the impact evidence {name}'s funding has contributed to. Because certifications and transparency matter.",
    public_ledger_text:
      'Every quarter we provide all of the impact evidence of our community has contributed to. Because certifications and transparency matter.',
    all: 'View all public ledgers',
  },

  TeamView: {
    pending_note_team: 'Pending team members become active in your next billing cycle.',
    pending_note_upgrade: 'Pending upgrades become active in your next billing cycle.',
    error:
      'An error occurred while upgrading your subscription. Please try again or contact us and we would be happy to help.',
    one: 'At least one team member required.',
    again: 'Try Again',
    support: 'Contact Support',
    success:
      'Your team has been successfully updated! Pending members / upgrades become active in your next billing cycle.',
  },

  TeamTotals: {
    header: 'Overall Impact',
    team: 'Active Team Members',
    carbon: 'Tonnes CO2*',
    plastic: 'Plastic Bottles*',
    trees: 'Trees Planted*',
    per_member: '*per Member / Month',
  },

  UpdatePlans: {
    your_team: 'Your Team',
    standard: 'Standard',
    superior: 'Superior',
    premium: 'Premium',
    adjust_standard: 'Adjust Standard',
    adjust_superior: 'Adjust Superior',
    adjust_premium: 'Adjust Premium',
    pending: 'Pending',
    standard_tooltip:
      'Offset 1.42 tonnes of CO2 and 100 plastic bottles each month. That`s more than the average persons footprint.',
    superior_tooltip:
      'Offset 2.84 tonnes of CO2 and 100 plastic bottles each month. That`s more than double the average person`s footprint.',
    premium_tooltip:
      'Offset 5.70 tonnes of CO2 and 100 plastic bottles each month. That`s four times the average person`s footprint.',
  },

  Upgrades: {
    your_upgrades: 'Your Upgrades',
    plastic_hero: 'Plastic Heroes',
    tree_planter: 'Trees Planters',
    adjust_plastic_hero: 'Adjust Plastic Heroes',
    adjust_tree_planter: 'Adjust Tree Planters',
    pending: '({count} Pending) *',
    plastic_hero_tooltip:
      'The global yearly average plastic use per capita is 65.7kg. In the Plastic Hero upgrade we have added 25% in order to ensure your team goes plastic positive! (250 bottles / month).',
    trees_tooltip:
      'Planting trees is one of the best climate solutions available today. An increase in forested areas by 25%, could erase nearly 100 years of emissions so go ahead and plant trees!',
    trees_tooltip_new:
      'Planting trees is one of the best climate solutions available today. An increase in forested areas by 25%, could erase nearly 100 years of emissions so go ahead and plant trees in Kenya with EarthLungs!',
    trees_tooltip_warning:
      'We’re changing our tree planting provider. Current pricing remains unchanged until end of March when it will update to {currencySymbol}{newTreePrice}.',
  },

  Cart: {
    confirm: 'Confirm',
    team_members: 'Total Team',
    standard: 'Standard',
    superior: 'Superior',
    premium: 'Premium',
    plastic_hero: 'Plastic Heroes',
    tree_planter: 'Trees Planters',
  },

  SupportedProjects: {
    header: 'Projects That Fight The Madness',
    text: 'You support certified projects that not only plant trees, prevent or sequester carbon and plastic but also have a long term positive social impact. Check out all of our projects here.',
  },

  ProjectReceipt: {
    download: 'View',
  },

  TransferredImpact: {
    header: 'Transferred Impact',
    text: 'Here you can see all of the receipts from your impact before joining Greenspark. The totals have been added to your dashboard and these receipts will be visible on your public page to show your stakeholders that you mean what you say!',
    text_live:
      'created impact before they joined Greenspark and has added it to their account from another source. See below for verified confirmation documents.',
  },

  CanceledView: {
    canceled: 'We’re sorry to see you go!',
    canceled_text:
      '<p>Your subscription with us has been canceled, but your insights can help us serve you and others better.</p><p>Please take a moment to tell us what we could have done differently <b>- it would be great to hear from you!</b></p>',
    feedback: 'Leave feedback',
    restart: 'Restart my subscription',
  },

  AccountType: {
    select: 'Select',
  },

  AllMemberImpactView: {
    our_impact: 'Our Impact',
    projects: 'Supported Projects',
    public_ledger: 'Public Ledger',
    home: 'Home',
    about: 'About Us',
    pricing: 'Pricing',
    login: 'Login',
    join: 'Start making a positive impact today!',
  },

  NetworkView: {
    header: 'Your impact network',
    text: 'Like with everything, we are stronger together. Easily refer, track and manage all of your partners here.',
    refer: 'Referrals',
    invite: 'Invite',
    partners: 'Partners',
    error: 'An error occurred, please try again or get in touch and we would be happy to help.',
    saved: 'Success!',
  },

  InvitePopUp: {
    grow_network: 'Grow your network',
    refer: 'Refer a business',
    refer_personal: 'Refer a friend',
    partner: 'Add partner',
  },

  ChoosePath: {
    referral: 'Refer a business',
    referral_text:
      'You’ll each get additional impact and help grow our community of like-minded companies.',
    button_text: 'Continue',
    partnership: 'Create a partnership',
    partnership_text: 'Invite your partners to fund their accounts and reward them with impact.',
  },

  Referrals: {
    back: 'Back',
    header: 'Refer a business',
    header_personal: 'Refer a friend',
    description: 'Help us reach more businesses to maximise our collective impact!',
    description_personal: 'Help us reach more people to maximise our collective impact!',
    how_it_works_1: 'Choose the impact you would like to be rewarded with',
    how_it_works_2: 'Share your unique link',
    how_it_works_3: 'When a business signs up, you both have an impact',
    how_it_works_3_personal: 'When a friend signs up, you both have an impact',
    choose: 'Choose your impact',
    choose_short: 'Choose impact',
    share: 'Share your referral link',
    copy: 'Copy',
    copied: 'Copied',
    plastic: '200 plastic bottles each',
    carbon: '1000kgs of CO2 each',
    trees: '30 trees each',
  },

  ReferralImpact: {
    header: 'Referral impact',
    empty: "You don't have any referrals yet.",
    empty1: 'Choose your impact and share your link to get started.',
    funded: 'Funded impact',
    funded_text:
      'The total amount of impact you have funded through referrals, this includes any impact given and received.',
    total: 'Total impact',
    total_text: 'The total amount of impact your referred users have had to date',
  },

  ReferralStats: {
    referrals: 'Referrals',
    partners: 'Partners',
  },

  ReferredUsers: {
    header: 'Referred users',
    empty: 'No referred users yet.',
    empty1: 'Share your referral link to get started',
    public_profile: 'Public Profile',
  },

  Partners: {
    create: 'Create partner',
    save: 'Update partner',
    partner_text: 'Total monthly partner cost:',
    one_time_text: 'Total cost',
    one_time_text_now: 'Total cost now',
    minimum: 'minimum',
    error: 'This partner already has a Greenspark account.',
    total_impact_discount: 'Total partner impact discount:',
  },

  PartnerInformation: {
    info: 'Partner information',
    first_name: 'First name*',
    last_name: 'Last name*',
    partner_name: 'Partner name*',
    email: 'Email*',
  },

  PartnerImpact: {
    header: 'Partner impact',
    funded: 'Funded impact',
    funded_text: 'The total amount of impact you have funded through partnerships.',
    total: 'Total impact',
    total_text: 'The total amount of impact your partners have had to date',
  },

  FundedImpact: {
    funded: 'Funded Impact',
    impact: 'Impact or allowance',
    length: 'Funding Length (months)',
    edit_help_text:
      'When editing partners only Account, Monthly allowance & Monthly impact can be changed. Any edits will be activated in your next funding cycle.',
    plan_type_label: 'Plan type',
    plan_type_help_link: 'Compare plan features',
    starter_business: 'Starter ({price}{currency} / month)',
    none: 'None',
    starter_yearly: 'Starter Yearly ({price}{currency} / month)',
    growth_business: 'Growth ({price}{currency} / month)',
    growth_business_yearly: 'Growth Yearly ({price}{currency} / month)',
    premium_business: 'Premium ({price}{currency} / month)',
    premium_business_yearly: 'Premium Yearly ({price}{currency} / month)',
    monthly_amount_label: 'Monthly amount({currency})',
  },

  ImpactInput: {
    impact: 'Choose impact',
    amount: 'Amount',
    add_impact: 'Add impact',
    remove_impact: 'Remove impact',
    carbon: 'Carbon - {price} / kilogram',
    plastic: 'Plastic Bottles - {price} / rescued bottle',
    trees: 'Trees - {price} / tree planted',
    trees_new: 'Trees in {country} - {price} / tree planted',
  },

  ReferralGrid: {
    partner_name: 'User name',
    added: 'Added',
    impact: 'Impact',
    status: 'Status',
    empty: 'You do not have any referrals yet.',
    empty1: 'Share your link to get started.',
    invite: 'Invite',
  },

  PartnerGrid: {
    partner_name: 'Partner name',
    added: 'Added',
    funding_end: 'Funding end',
    impact: 'Funding type',
    status: 'Status',
    empty: 'You do not have any partners yet.',
    empty1: 'Invite one to get started.',
    invite: 'Invite',
  },

  Reward: {
    reward: 'Reward partner',
    text: 'Choose the impact(s) you would like to reward your partner with. They will be notified and the impact will automatically be added to their account.',
    total: 'Total cost:',
    minimum: 'minimum',
    description: 'Add a personal message',
    total_impact_discount: 'Total partner impact discount:',
  },

  StopPartnership: {
    stop: 'Stop partnership',
    text: 'By proceeding, you will stop funding your partner’s account.  This can’t be un-done and means they will retain access to their account, but no further impact will be billed to you.',
  },

  History: {
    history: 'Funding history',
    paid: 'Date paid',
    funding: 'Type',
    funding_amount: 'Amount',
    status: 'Payment',
    partner_name: 'Funded by',
    empty: 'You do not have any funding yet.',
    empty1: 'Reward them to get started.',
  },

  Edit: {
    edit: 'Edit partner',
  },

  SignupPreInfo: {
    welcome: 'Welcome to Greenspark!',
    subtext:
      'Activate your account in order to track your partner funded impact and create positive change in the world.',
    how_it_works: 'How Greenspark works',
    one: 'Activate your account.',
    two: 'Track your partner funded impact, create impact of your own, and share it with your community. ',
    three: 'Make the world a better place together!',
    continue: 'Continue',
    error: 'This partner account has already been claimed. Proceed to ',
  },

  SignupInfoPartners: {
    back: 'Back',
    create_account: 'Activate Account',
    first_name: 'First name',
    last_name: 'Last name',
    email: 'Email',
    company_name: 'Company name*',
    vat_id: 'VAT Number (EU Only)',
    password: 'Password',
    password_hint:
      'Your Password must be at least 6 letters long, contain at least one special character and a mix of upper/lower case letters.',
    continue: 'Activate account',
  },

  PartnerSuccessView: {
    choose: 'Choose your impact',
    success: 'Sign up successful!',
    update: 'Update impact',
    monthly_impact:
      '<p>{connectorName} is funding your account until {fundingEnds}. Check out your monthly impact below!</p>',
    monthly_impact_ongoing:
      '<p>{connectorName} is funding your account. Check out your monthly impact below!</p>',
    monthly_allowance:
      '<p>{connectorName} is funding your account with {allowanceAmount} Greenspark credits per month until {fundingEnds}. Use your credits to claim your impact. We have made a pre-selection for you!</p>',
    monthly_allowance_ongoing:
      '<p>{connectorName} is funding your account with {allowanceAmount} Greenspark credits per month. Use your credits to claim your impact. We have made a pre-selection for you!</p>',
    subscription:
      '<p>{connectorName} is funding your <b>{planName}</b> plan until {fundingEnds}. Check out your impact!</p>',
    subscription_ongoing:
      '<p>{connectorName} is funding your <b>{planName}</b> plan. Check out your impact!</p>',
    onetime_impact:
      '<p>{connectorName} is funding your account until {fundingEnds}. Check out your impact!</p>',
    onetime_impact_ongoing:
      '<p>{connectorName} has funded your account with impact. Check it out below!</p>',
    onetime_allowance:
      '<p>{connectorName} is funding your account with {allowanceAmount} Greenspark credits until {fundingEnds}. Use your credits to claim your impact. We have made a pre-selection for you!</p>`',
    onetime_allowance_ongoing:
      '<p>{connectorName} is funding your account with {allowanceAmount} Greenspark credits. Use your credits to claim your impact. We have made a pre-selection for you!</p>',
    plan_type_help_link: 'Compare plan features',
  },

  ChooseAllowance: {
    total: 'Total Greenspark credits left:',
    continue: 'Save & continue',
    note: 'Please use all of your Greenspark credits to proceed.',
    error: 'An error occurred when claiming your allowance. Please try again or get in touch.',
  },

  NoAllowance: {
    continue: 'Continue to dashboard',
  },

  TotalFunding: {
    trees: 'planted per month',
    tree: 'Trees',
    trees_once: 'planted now',
    plastic: 'Plastic bottles',
    plastic_bottles_once: 'rescued now',
    plastic_bottles: 'rescued per month',
    co2: 'offset per month',
    co2_once: 'offset now',
    carbon: 'kg of CO2',
  },

  CheckoutView: {
    header: 'Add your payment details',
    pay: 'Confirm payment',
    subscribe: 'Start your impact journey',
    total: 'Total',
    total_today: 'Total due today',
    add_card: 'Save payment card',
    payment_on_file:
      'You are about to subscribe to a new plan which will replace your current one. You will be charged at the new rate every month.',
    free_trial: '14 day free trial',
    customisable: 'Easy setup',
    easy_setup: 'Fully customisable',
  },

  PaymentSuccessView: {
    continue: 'Continue to dashboard',
    payment: 'Go to payment',
  },

  AddCardPopUp: {
    header: 'Add your payment details',
    text_integration:
      'In order to add an integration you’ll need to set up a method of payment. This is so you can create impact automatically as soon as your integration is live. You’ll be billed each month for the impact you have created.',
    text_teams:
      'In order to add team members you’ll need to set up a method of payment. This is so you can create impact automatically. You’ll be billed each month for the impact you have created.',
    text_partner:
      'In order to add or reward partners you’ll need to set up a method of payment. This is so you can pay for the cost of each reward or partner. You’ll be billed each month for all of the rewards and partners you create.',
    text_upgrade:
      'In order to add an integration you will need to subscribe to a plan. If you had active integrations, then they have been paused until you upgrade.',
    button: 'Add payment details',
  },

  UpgradeCard: {
    highlight: 'Most popular plan',
    current: 'Current Plan',
    upgrade: 'Upgrade to {plan}',
    downgrade: 'Downgrade to {plan}',
    select: 'Select',
    start_free: 'Start free',
    growth_business: 'Growth',
    growth_business_yearly: 'Growth',
    starter_business: 'Starter',
    starter_business_yearly: 'Starter',
    premium_business: 'Premium',
    premium_business_yearly: 'Premium',
    enterprise_business: 'Enterprise',
    earth_hero: 'Earth Hero',
    plastic_hero_personal: 'Plastic Hero',
    personal: 'Personal',
    trial: '14 day free trial',
    pending: 'Pending',
    book_demo: 'Book a demo',
    free: 'Free',
    free_business: 'Pay-as-you-grow',
    impact_price_warning:
      '<strong>Impact pricing is increased</strong> on this plan. Check our <a href="{href}">detailed pricing table.</a>',
  },

  ShopifyPopUp: {
    header: 'Upgrade plan',
    text: 'Since your billing is running through Shopify, the new plan will be added to your Shopify bill as a Usage Charge. Please confirm that you are happy to move ahead.',
    upgrade: 'Confirm & upgrade',
  },

  SaveButton: {
    save: 'save',
    disabled: 'minimum {limit}{currency}',
  },

  WarningDialog: {
    warning_text:
      'By regenerating your {apiKeyName} key, the previous key will be disabled. Any processes which use your API key should be updated to use the new one.',
    checkbox_label: 'Never show this message again',
    accept: 'understood',
    cancel: 'cancel',
  },

  ImpactWalletView: {
    greeting_title: 'Hey {username}!',
    greeting_text:
      'Welcome back. On this dashboard you can track the impact of all of your impact actions from Greenspark retailers.',
    updates: 'Updates',
    claim_impact: 'Claim impacts',
    impact_actions: 'Impact actions',
    brands: 'Brands',
    order_details_title: 'Order from {companyName}',
    brand_details_title: 'Your impact with {companyName}',
  },

  NewsFeed: {
    updates: 'Updates',
    intro_title: 'Why {companyName} joined Greenspark',
  },

  NewsFeedItem: {
    order_text: 'You placed and order with {companyName}. With this order you will ',
    url_label_target: 'Learn more about the {companyName} sustainability commitment',
    url_label_order: 'View order',
    url_label_project: 'View order',
    url_label_introduction: 'View {companyName} public profile',
    did_you_know: 'Did you know?',
  },

  LatestImpactActionsList: {
    latest_orders: 'Latest impact actions',
    see_all: 'see all',
  },

  ImpactActions: {
    title: 'Impact Actions',
    dates: 'Choose date',
    no_result:
      'There are no impact actions with the current filter settings. Please, set up a different search!',
  },

  ImpactsByBrands: {
    title: 'Brands',
  },

  ImpactsByBrandsItem: {
    flavor_text: 'Your impact with',
  },

  ImpactActionsItem: {
    title: 'Impact Actions',
    product: '{count} product | {count} products',
    per_subscriber: 'Newsletter subscription',
    per_product_review: 'Review',
    per_company_review: 'Review',
    one_off: 'Tailored impact action',
    referral: 'Referral',
    subscription: 'Subscription',
    gift: 'Gift',
    purchase: 'Purchase',
    loyalty_points: 'Loyalty points',
    form: 'Completed form',
  },

  ImpactActionDetailDialog: {
    product_info_title: 'Products in Your order',
    overall_impact_title: 'Overall impact with {companyName}',
    view_details: 'View details',
    view_your_impact: 'View Your Impact',
    order_created: 'Order from {sellerUserName}',
    per_subscriber: "Subscription to {sellerUserName}'s newsletter",
    per_company_review: 'Review on {sellerUserName}',
    per_product_review: 'Review on a product at {sellerUserName}',
    subscription: 'Subscription',
    one_off: 'Tailored impact action',
    referral: 'Referral',
    gift: 'Gift',
    purchase: 'Purchase',
    loyalty_points: 'Loyalty points',
    form: 'Completed form',
  },

  BrandDetailDialog: {
    orders_by_brand_title: 'Impact actions',
    product_amount_in_order: '{count} product | {count} products',
    brand_intro_title: 'Why {companyName} joined Greenspark',
    intro_link_text: 'See our impact',
    per_subscriber: 'Newsletter subscription',
    per_product_review: 'Review',
    per_company_review: 'Review',
    purchase: 'Purchase',
    gift: 'Gift',
    loyalty_points: 'Loyalty points',
    form: 'Completed form',
    referral: 'Referral',
    subscription: 'Subscription',
    one_off: 'Tailored impact action',
  },

  CustomerEngagementPortal: {
    back: 'Back',
    title: 'Post purchase engagement',
    subtitle:
      'Engage with your customers through impact. Send automated certificates and dashboards where your customers can track the impact of their purchases.',
    set_targets: 'Set targets',
    upload_logo: 'Upload logo',
    activate_dashboard_title: 'Activate customer dashboard',
    activate_dashboard_text:
      'Once your customer portal is active, whenever you create impact for your customers it will be recorded in their own personal dashboard. Your customers will be notified by email including a certificate whenever new impact is generated.',
    intro_title: 'Tell your customers why you joined Greenspark!',
    set_intro_text:
      'This will be shown as a message in your customer’s feed. Engage them in your sustainability mission.',
    target_goal_title: 'Set your sustainability targets',
    set_target_goal_text:
      'Set up sustainability targets that you would like to share with your customers. These will be pushed into the newsfeed within their portal.',
    upload_logo_title: 'Upload your logo',
    upload_logo_text: 'Upload your logo to ensure it is shown in any customer communications.',
    reengage_title: 'Re-engage your customers',
    reengage_text: 'Re-engage your customers with special impact promotions.',
    coming_soon: 'Coming soon!',
    certificate_title: 'Example certificate',
    certificate_button: 'View example certificate',
    custom_domain_title: 'Custom domain',
    custom_domain_text:
      'Increase deliverability and conversions by sending your post purchase emails from your own domain.',
    custom_domain_alert: {
      not_valid: 'Your custom domain is not valid. Please update and verify it now.',
      activate_email_campaigns:
        'To activate your email campaigns, you need to set up a custom domain first.',
    },
    set_domains_button: 'Set domains',
    notification_template_title: 'Email campaigns',
    notification_template_text:
      'Edit the content of the emails sent to your customers or simply use our pre-defined templates.',
    manage_campaign: {
      title: 'Choose how to manage your post purchase campaign:',
      email_platform: 'Through your email marketing platform',
      greenspark: 'Through Greenspark directly',
    },
    impact_wallet: 'Greenspark Impact Wallet',
    email_sync_title: 'Sync data with your email marketing platform',
    email_sync_text:
      'Manage all of your customer impact directly within your email marketing platform. Use the data to create flows, segmentation and more.',
  },

  AlertPanel: {
    alert_title: 'You have no active impact triggers',
    alert_text:
      'Activate an impact automation trigger to gain access to the post purchase engagement.',
    link_text: 'Activate Triggers',
  },

  NavBar: {
    refer_business: 'Refer a business ',
    refer_friend: 'Refer a friend',
    new_tree_provider_info_1: 'Important Update: Changes to Tree Provider & Pricing.',
    new_tree_provider_info_2: 'Click here to read about it.',
  },

  SelectPlanView: {
    upgrade_title_personal: 'Choose your subscription plan',
    upgrade_title_business: 'Start adding positive impact to your business today',
    upgrade_text_personal:
      'Go Earth Positive by funding vetted projects that prevent plastic from entering our oceans and offset more than your carbon and plastic footprints.',
    upgrade_text_business:
      "Choose the perfect plan for your business and take tangible, sustainable action that’s great for the planet and your company's performance. Having a positive impact has never been easier!",
    monthly: 'Monthly',
    yearly: 'Yearly',
    save: '(save up to 25%)',
    compare: 'Compare plan features',
    hide: 'Hide plan features',
    compare_plan_features: 'Compare plan features',
    enjoy_paid_plan_benefits: 'Enjoy these amazing benefits with all paid plans:',
    positive_impact: 'Positive Impact',
    support_verified_projects:
      'Support verified projects that reduce global emissions and benefit the environment.',
    private_dashboard: 'Private Dashboard',
    track_real_time_impact:
      'Track your real-time impact and achievements, and inspire others by sharing your journey.',
    integration_with_automation: 'Integration with Automation',
    easily_integrate_sustainability:
      'Easily integrate sustainability into your processes with efficient automation tools.',
    bonus_tree_planting: 'Bonus Tree Planting',
    well_plant_tress:
      "We'll plant extra trees for you to promote reforestation and environmental restoration.",
    have_more_questions: 'Have more questions?',
    get_in_touch: 'Get in touch',
    book_a_demo: 'Book a Demo!',
    months_free:
      'Get {amount} month&nbsp;<strong>free!</strong> | Get {amount} months&nbsp;<strong>free!</strong>',
  },

  PlanComparisonTable: {
    plan_name_free: 'Pay-as-you-grow',
    plan_description_free:
      'A good way to <strong>add positive impact</strong> that your customers will love from day one.',
    plan_name_starter: 'Starter',
    plan_preface_starter: 'All Free features, plus:',
    plan_description_starter:
      'For <strong>individuals or teams</strong> just getting started with positive impact.',
    plan_name_growth: 'Growth',
    plan_description_growth:
      'Perfect for <strong>growing businesses</strong> seeking to amplify their impact with advanced tools and team collaboration.',
    plan_preface_growth: 'All Starter features, plus:',
    plan_name_premium: 'Premium',
    plan_description_premium:
      'Unlock the full potential of your impact journey with our premium plan designed for <strong>ambitious businesses.</strong>',
    plan_preface_premium: 'All Growth features, plus:',
    plan_preface_enterprise: 'Includes all Premium features +',
    plan_name_enterprise: 'Enterprise',
    plan_description_enterprise:
      'Empower your enterprise for meaningful change with <strong>limitless possibilities</strong> and dedicated support.',
    free: 'Free',
    start_free: 'Start Free',
    select: 'Select',
    pending: 'Pending',
    request_demo: 'Request a Demo',
    current_plan: 'Current plan',
    see_plan_details: 'See plan details',
    custom: 'Custom',
    basic: 'Basic',
    full: 'Full',
    calls: '{count} calls',
    priority: 'Priority',
    standard: 'Standard',
    detailed_plan_comparison: 'Detailed plan comparison',
    features: 'Features',
    engagement_hub: 'Engagement Hub',
    integrations_and_automations: 'Integrations & Automations',
    network: 'Network',
    analytics: 'Analytics',
    api: 'API',
    support: 'Support',
    number_of_users: 'Number of users',
    dashboard_access: 'Dashboard access',
    offset_your_teams_emissions: 'Offset your team’s emissions',
    add_monthly_subscriptions:
      'Add monthly subscriptions to offset your team members carbon and plastic footprints.',
    impact_marketplace: 'Impact marketplace',
    easily_access_projects:
      'Easily access all of our climate projects including forestry, plastic recovery, CO2 offsetting, seaforestation, water and bees.',
    marketing_calendar: 'Marketing calendar',
    number_of_extra_trees_per_month: 'Number of extra trees per month',
    public_profile: 'Public profile',
    create_sustainability_page:
      'Create your own sustainability page to track and share your impact with your community.',
    badges_and_widgets: 'Badges and widgets',
    showcase_your_impact:
      'Showcase your impact across the full customer journey with dynamic widgets that incentivise action.',
    qr_code_for_products: 'QR Code for physical products',
    bridge_the_gap:
      'Bridge the gap between digital and physical elements of your impact story. Create and customise QR codes to use on swing tags, flyers, business cards, or in-store merchandising.',
    customer_impact_wallet: 'Customer impact wallet',
    connect_with_your_customers:
      'Connect with your customers on a new level by bringing them in on your impact story. Give them the ability to track their impact with a unique profile, send automated certificates, project and campaign updates.',
    media_pack: 'Media pack',
    active_integrations: 'Active integrations',
    integrate_your_favourite_apps: 'Integrate your favourite apps to set your impact on autopilot.',
    basic_automations: 'Basic automations',
    basic_automations_include:
      'Basic automations include email, reviews and e-commerce (per order only) automations.',
    advanced_automations: 'Advanced automations',
    includes_all_automations: 'Includes all available automations.',
    referral_program: 'Referral program',
    give_and_receive_impact:
      'Give and receive free environmental impact with every partner you successfully refer.',
    partner_rewards: 'Partner rewards',
    invite_your_partners:
      'Invite your partners into your impact network. Strengthen partnerships by rewarding them with climate impact.',
    basic_analytics: 'Basic analytics',
    gain_better_understanding:
      'Gain a better understanding of the impact you’ve made and what that means for your business.',
    advanced_analytics_name: 'Advanced analytics',
    deep_dive_into_analytics:
      'Deep dive into your impact analytics and discover how your community is engaging with your environmental mission.',
    widget_api: 'Widget API',
    display_our_widgets:
      'Easily display our widgets and badges anywhere or create your own. The Widget API provides you full design flexibility.',
    greenspark_api: 'Greenspark API',
    integrate_impact_anywhere:
      'Seamlessly integrate impact anywhere with the Greenspark API. Fully white-label our solution into any business use case.',
    chat_and_email: 'Chat and email',
    technical_support: 'Technical support',
    priority_campaign_support: 'Priority campaign support',
    receive_support:
      'Receive 1-2-1 support from our marketing team to help you successfully run impact based campaigns.',
    impact_pricing: 'Impact Pricing',
    free_plan: 'Free plan',
    paid_plans: 'Paid plans',
    paid_plan_list: '(Starter, Growth & Premium)',
    enterprise: 'Enterprise',
  },

  FrequentlyAskedQuestions: {
    title: 'Your questions, answered',
    personal: {
      item_1: {
        question: 'What happens when I subscribe?',
        answer:
          '<p>Simply select your subscription plan, enter payment information, and we’ll do the rest! Your funds will directly support earth and plastic positive initiatives to reduce carbon emissions and plastic pollution. You can log into your personal dashboard to see your impact grow!</p>',
      },
      item_2: {
        question: 'Do I have to sign a long-term contract?',
        answer: '<p>All of our plans are on a monthly basis and can be cancelled at any time.</p>',
      },
      item_3: {
        question: 'How big is my carbon footprint?',
        answer:
          '<p>To calculate your specific carbon footprint, here is a good calculator from the <a href="https://footprint.wwf.org.uk/" target="_blank">WWF</a>.  Don’t have time to calculate your exact footprint? Here are some national annual averages that may help:</p><p class="footprint">KUWAIT: 25.8 tonnes of CO2</p><p class="footprint">USA: 16.5 tonnes of CO2</p><p class="footprint">UK: 6.5 tonnes of CO2</p><p class="footprint">BHUTAN: 1.4 tonnes of CO2</p>',
      },
      item_4: {
        question: 'How can I be sure these projects will stop climate change?',
        answer:
          '<p>While we can’t promise to “stop” climate change, we can promise that your funds are directly supporting earth-positive projects vetted by the United Nations Framework Convention on Climate Change and The Plastic Bank. Find out more about the climate change fighting, madness stopping, earth loving projects that the Greenspark community has supported here.</p>',
      },
      item_5: {
        question: 'What is a Greenspark Footprint?',
        answer:
          '<p>1 Greenspark Footprint = 17 tonnes of CO2.  We created the “Greenspark Footprint” based on global averages and data to ensure that every Greenspark member is effectively offsetting a sufficient amount of Carbon Dioxide to fight climate change.</p><p>Want more detail? 1 Greenspark Footprint is calculated to align with the climate goal to keep global temperatures from rising more than 1.5C by 2030, as set about by the United Nations Framework Convention on Climate Change (UNFCCC) <a href="https://unfccc.int/process-and-meetings/the-paris-agreement/the-paris-agreement" target="_blank">Paris Agreement </a>in 2016. According to the latest <a href="https://wedocs.unep.org/bitstream/handle/20.500.11822/30797/EGR2019.pdf?sequence=1&amp;isAllowed=y" target="_blank">Emissions Gap Report 2019</a> published by the <a href="https://www.unenvironment.org/" target="_blank">United Nations Environment Programme</a>, we must reduce global emissions by 55% to stay on track with the goals set out in the Paris Agreement to mitigate global temperature rise. The report also highlights the fact that higher income countries emit a disproportionate amount of global Green House Gases, with G20 nations accounting for nearly 80% of global emissions. Therefore, to calculate our Greenspark Footprint, we used <a href="https://data.worldbank.org/indicator/EN.ATM.CO2E.PC" target="_blank">World Bank data</a> to determine the average per capita carbon footprint of High Income Nations, which is 10.9 tonnes of CO2 per capita. We then increased this figure by an additional 55%, to stay in line with the Paris Agreement as outlined by the UNEP Emissions Gap Report. This brings the total necessary carbon offset to 16.86 (10.9+5.96), rounded up to 17 tonnes of CO2 for greater impact. While all data sets and averages have limitations, we will always be 100% transparent in our calculations and will continue to re-evaluate our methodology using trusted data sources, so we can effectively fight the climate madness together.</p>',
      },
      item_6: {
        question: 'What else can I do to help end the climate crisis?',
        answer:
          '<p>Greenspark is a great step towards creating a culture that values sustainability in your workplace. But it’s not the only answer! Talk with your team about what other steps you can take as a company to reduce your impact and how you can further align your business practices with the Paris Climate Agreement.</p>',
      },
      item_7: {
        question: "What's the difference between plans?",
        answer:
          '<p>All of our plans support vital earth-positive projects tackling plastic and carbon pollution. The difference between the three personal plans we offer is the amount of plastic and carbon you wish to offset. Do you travel a lot? Then go for the Earth Hero Plan, or if you live a more sedentary lifestyle than the Personal plan will more than cover your emissions. To learn more about our business plans, read more <a href="https://www.getgreenspark.com//pricing/business" target="_blank">here</a>.</p>',
      },
      item_8: {
        question: 'Why did we create Greenspark?',
        answer:
          '<p>We created Greenspark to fight the climate madness! After finding it difficult (and expensive) to calculate and offset our own carbon footprints, we decided to take matters into our own hands and create Greenspark! We know that despite how much we may try, we can’t all be perfect. And while we don’t see this as a replacement for trying to live a more sustainable life, we think it’s a pretty awesome way to give back and help create a more healthy future for our planet.</p>',
      },
    },
    business: {
      item_1: {
        question: 'Which plan is best for me?',
        answer:
          'The best plan for you will depend on the type of business, your industry and where you are on your sustainability journey. Use the comparison table above to see which plan fits your needs best for you, or contact us and we would be happy to help you choose.',
      },
      item_2: {
        question: 'Do I have to sign a long term contract?',
        answer:
          'All of our plans are available on a monthly or yearly basis and can be cancelled at any time.',
      },
      item_3: {
        question: 'How can I trust the projects you support to benefit people and the planet?',
        answer:
          "We have two levels of authentication to ensure we're as transparent as possible. Firstly we only work with highly vetted, verified and accredited projects. They're all UN accredited and continually audited to ensure they are delivering the impact that they report.  Our second layer of transparency is our public ledger, which can be found in your dashboard and at the link here. This shows an open record of all the transactions between us and those project partners so you can see exactly where the funds are going.",
      },
      item_4: {
        question:
          'I made a mistake while signing up for Greenspark and would like to change my subscription. How can I do it?',
        answer:
          'You can easily update your subscription directly from your Greenspark dashboard. Please note that if you’re currently on a free plan or trial, switching to a paid plan will result in an immediate charge for the selected plan. If you have any questions, feel free to reach out—we’re happy to help!',
      },
      item_5: {
        question: 'I would like to downgrade my plan. How can I do it?',
        answer:
          'To downgrade your plan, simply go to the billing section in your Greenspark dashboard. Please note that if you’re currently using features exclusive to paid plans, you’ll need to disable these before downgrading. Once you’ve made the change, you can downgrade your account, and any paid features will no longer be available. If you have questions or need assistance during this process, don’t hesitate to reach out—we’re here to help!',
      },
      item_6: {
        question: 'How can I estimate my impact volume?',
        answer:
          'This number depends on several factors, but generally it’s calculated by multiplying your number of monthly events (for example orders, newsletter signups, etc) by your chosen impact (planting trees, rescuing plastic, offsetting carbon) that you perform each month. We’re here to help you figure this out! Get in touch.',
      },
      item_7: {
        question:
          'There’s a particular integration I’m interested in adding to my Greenspark account. Where can I see the integrations list and/or make a request to add a new one?',
        answer:
          "You can view all available integrations on our dedicated integrations page. If the integration you're interested in isn't listed, feel free to reach out to us—we're always open to exploring new possibilities and would love to hear your suggestions!",
      },
      item_8: {
        question:
          'I have positive impact with another provider, how can I display that in my Greenspark account?',
        answer:
          'If you’ve created positive impact with another provider and would like it displayed in your Greenspark account, simply reach out to us! Our team can quickly add this to your account so everything is showcased in one place.',
      },
      item_9: {
        question: 'How can I trust Greenspark to ensure my money reaches the projects I support?',
        answer:
          "Greenspark is not simply an app in the app store, we're a VC-backed business operating with a team of experienced entrepreneurs who are working to reshape the way businesses across the world take sustainable action. We strive to be as transparent as possible with our customers and keep a public ledger of every payment we have made to projects so that you can be sure where your money goes. You can view our public ledger here.",
      },
    },
    questions: 'Do you have more questions?',
    get_in_touch:
      'Browse our <a href="https://help.getgreenspark.com" target="_blank">Knowledge Base</a> for answers and resources. If your question remains unanswered or you need further assistance, don\'t hesitate to get in touch with',
    support_team: 'our dedicated support team.',
  },

  WelcomeView: {
    welcome_title: 'Your {0} impact with {1}',
    welcome_text: 'Discover more about your impact by creating a free account.',
    tackle_climate_change_title: 'Tackle <b class="green-text">climate change</b> every day',
    tackle_climate_change_text:
      'Start your journey towards a sustainable life & create Earth Positive impact around the world.',
    checklist_text_1:
      'Your own personalised (and free!) dashboard, where you can <span class="green-text">track & share all of your environmental impact</span> whether it\'s trees planted, plastic rescued or carbon offset.',
    checklist_text_2:
      'A place to store <span class="green-text">certificates and badges</span> for all your environmental achievements.',
    checklist_text_3:
      '<span class="green-text">Regular updates</span> on the projects you\'re supporting and the latest in sustainability news.',
    checklist_text_4:
      'Add your own specific impact, <span class="green-text">support your mission further</span> and take part in more sustainable activities.',
    support_title: 'Support <span class="green-text">accredited</span> climate action',
    companyName: '{companyName}',
    order_created: 'order',
    per_company_review: 'company review',
    per_product_review: 'product review',
    per_subscriber: 'newsletter subscription',
    invoice: 'purchase',
    redeem_loyalty_points: 'loyalty points',
    impact_title: 'Your impact',
    continue: 'Continue',
    login_text: 'Already a member?',
    login_link: 'Login',
    activate_dashboard: 'Activate your dashboard',
    make_difference_title: 'What difference is your impact making?',
    track_impact_title: 'Track your Earth Positive impact today',
    form: 'completed form',
  },

  SignupB2B2C: {
    create_account: 'Create account',
    email: 'Email',
    password: 'Password',
    login_text: 'Already a member?',
    login_link: 'Login',
    password_hint:
      'Your Password must be at least 6 letters long, contain at least one special character and a mix of upper/lower case letters.',
    already_used: 'Email already used.',
    already_account: 'Do you already have an account with us? If so, please login.',
    first_name: 'First Name',
    last_name: 'Last Name',
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    accept: 'Accept our {0} & {1}',
  },

  SuccessB2B2C: {
    title: 'Thanks For Signing Up!',
    text: 'Use your Greenspark dashboard to track the impact of your purchases from participating retailers, claim additional impact, and learn about the mission behind each brand you support.',
    greeting: 'We can’t wait to see your impact grow!',
    button_text: 'Continue to Dashboard',
  },

  SettingCard: {
    active: '{count} active',
    not_valid: 'Not valid',
    required: 'Mandatory',
  },

  IntroSettingDialog: {
    description:
      'Let your customers know why you joined Greenspark and engage them on your sustainability mission.',
    placeholder:
      'We joined Greenspark to ensure that we maximise our positive impact on our planet and it’s people and are excited to have you on this journey with us.',
    text_saved: 'Introduction saved successfully',
  },

  SustainabilityGoals: {
    header_title: 'Sustainability goals',
    new_goal: 'New goal',
    goal_saved: 'Your sustainability target is successfully saved',
    goal_updated: 'Your sustainability target is successfully updated',
    goal_deleted: 'Your sustainability target is deleted',
    input_label: 'Internal Goal Name',
  },

  SustainabilitySiteSetup: {
    title: 'Link to your sustainability page',
    description:
      'Create another touchpoint with your users by linking to your sustainability page. We will add it to your sustainability updates.',
    input_label: 'Sustainability page url',
    input_placeholder: 'getgreenspark.com/projects',
    url_saved: 'Sustainability page link successfully saved',
  },

  TargetGoalSettingDialog: {
    description:
      'Set up to five sustainability targets that you would like to share with your customers and engage them on your sustainability mission. These will be pushed into the newsfeed within their portal.',
  },

  DemoFeed: {
    title: 'Feed example',
    description: 'This is what the message will look like in your  customer’s feed.',
  },

  CommonUi: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    manage: 'Manage',
    add: 'Add',
    back: 'Back',
    next: 'Next',
    continue: 'Continue',
    copy: 'Copy',
    sync: 'Sync data',
    is_copied: 'Copied!',
    copy_title: 'Copy to clipboard',
    logout: 'Logout',
    error_generic_payment:
      'An error occurred with your payment. Please try again or contact us and we would be happy to help.',
    error_generic:
      'An error occurred, please try again or get in touch and we would be happy to help.',
    success_generic_payment: 'Payment successful',
    success_automation_updated: 'Automation successfully updated',
    success_trigger_updated: 'Trigger successfully updated',
    success_source_updated: 'Source successfully updated',
    success_automation_deleted: 'Automation successfully deleted',
    error_generic_auth: 'Your session expired, please log in again.',
    store_already_connected: 'This integration is already connected to a Greenspark account.',
    old_version_alert:
      "It looks like you're using an older version of the application. Please refresh the page to update to the latest version. If the issue persists, please reauthenticate to continue.",
    test_connection: {
      error: {
        INTEGRATION_READ_DIRECTION_TEST_FORBIDDEN:
          'The API key provided doesn’t have read permissions. Please update it by clicking here.',
      },
    },
  },

  Certificate: {
    description:
      'Each of your customers receives a downloadable certificate. They are sent out with impact notifications and can be downloaded by customers as an image.',
    impact_text: '2 trees planted and 1 plastic bottle rescued',
    cert_description:
      'This certifies that 2 trees will be planted and 1 plastic bottle rescued in partnership with Greenspark.',
  },

  AnalyticsView: {
    title: 'Analytics',
    description: 'Keep track of your impact metrics here.',
    overview_title: 'Reports overview',
    general: 'General',
    referrals: 'Referrals',
    partners: 'Partners',
    public_page: 'Public page',
    qr_code: 'QR code',
    automations: 'Automations',
    subscription_and_tailored_impact: 'Subscription & tailored impact',
    live_page: 'Public page',
    customer_engagement: 'Customer engagement',
  },

  AnalyticsDashboard: {
    dates: 'Choose date',
    upgrade_text: 'Want more in-depth analytics?',
    upgrade: 'Upgrade now',
    call_to_upgrade_card_title: 'Want to more in-depth analytics?',
    call_to_upgrade_card_text:
      'Upgrade to a <span>Growth</span> or <span>Premium</span> plan to gain access to our full suite of analytics.',
  },

  UpgradeCardList: {
    per_month: 'per month',
    per_year: 'per month, billed yearly',
    personal: {
      plastic_bottles: '<b>100 plastic bottles</b> removed each month',
      greenspark_footprint: '<b>1 Greenspark Footprint</b>removed / year',
      plant_trees: 'Plant trees and remove additional plastic',
      track_share: '<b>Track & share</b> your impact',
    },
    plastic_hero_personal: {
      plastic_bottles: '<b>350 plastic bottles</b> removed each month',
      greenspark_footprint: '<b>1 Greenspark Footprint</b> removed / year',
      plant_trees: 'Plant trees and remove additional plastic',
      track_share: '<b>Track & share</b> your impact',
    },
    earth_hero: {
      plastic_bottles: '<b>350 plastic bottles</b> removed each month',
      greenspark_footprint: '<b>4 Greenspark Footprints</b> removed / year',
      plant_trees: 'Plant trees and remove additional plastic',
      track_share: '<b>Track & share</b> your impact',
    },
    free: {
      info: 'A good way to <b>add positive impact</b> that your customers will love from day one.',
      create_impact: 'Create and manage impact with <b>vetted climate projects</b>',
      users: '<b>1 active user</b>',
      integration: '<b>1 active app integration</b> with basic automations',
      public_profile:
        'Public profile, dynamic widgets and badges to <b>showcase and share your impact</b> to customers',
      team_offset: "Offset your team's carbon and plastic footprints",
      analytics: 'Basic analytics to track your impact',
    },
    starter: {
      info: 'For <b>individuals or teams</b> just getting started with positive impact.',
      everything: '<b>All Free features, plus:</b>',
      impact_price: '<b>Reduced impact pricing</b>',
      media_pack: '<b>Media pack</b> of shareable content',
      extra_tree: '<b>1 extra tree</b> planted each month',
    },
    growth: {
      everything: '<b>All Starter features, plus:</b>',
      active_integration: '<b>Up to 3 integrations</b> with advanced automations',
      users: '<b>Up to 3 user</b> account',
      engagement_tools: '<b>Full suite of engagement tools</b> to share your positive impact',
      network: '<b>Network feature</b> to reward partners',
      advanced_analytics: '<b>Advanced</b> analytics',
      extra_trees: '<b>25 extra trees</b> planted each month',
    },
    growth_new: {
      info: 'Perfect for <b>growing businesses</b> seeking to amplify their impact with advanced tools and team collaboration.',
      everything: '<b>All Starter features, plus:</b>',
      active_integration: '<b>Up to 3 integrations</b> with advanced automations',
      users: '<b>Up to 3 user</b> account',
      engagement_tools: 'Basic <b>post purchase engagement</b>',
      public_profile: '<b>Customisable public profile</b>',
      network: '<b>Network feature</b> to reward partners',
      advanced_analytics: '<b>Advanced</b> analytics',
      qr_code: '<b>QR codes</b> for seamless physical engagement',
      extra_trees: '<b>15 extra trees</b> planted each month',
    },
    premium: {
      everything: '<b>All Growth features, plus:</b>',
      active_integration: '<b>Unlimited</b> integrations with advanced automations',
      users: '<b>Up to 8 user</b> account',
      full_api: '<b>Full API</b> access for custom integrations.',
      full_media: '<b>Full</b> media pack',
      priority_technical: '<b>Priority technical</b> and campaign support',
      enhanced_onboarding: '<b>Enhanced</b> onboarding',
      extra_trees: '<b>50 extra trees</b> planted each month',
    },
    premium_new: {
      info: 'Unlock the full potential of your impact journey with our premium plan designed for <b>ambitious businesses.</b>',
      everything: '<b>All Growth features, plus:</b>',
      active_integration: '<b>Unlimited</b> integrations with advanced automations',
      users: '<b>Up to 8 user</b> account',
      full_api: '<b>Full API</b> access for custom integrations.',
      post_purchase: 'Advanced <b>post purchase engagement</b>',
      priority_technical: '<b>Priority technical</b> and campaign support',
      enhanced_onboarding: '<b>Enhanced</b> onboarding',
      extra_trees: '<b>30 extra trees</b> planted each month',
    },
    enterprise: {
      everything: '<b>All Premium features, plus:</b>',
      api_calls: '<b>Unlimited</b>  API calls',
      users: '<b>Unlimited</b> user account',
      success_manager: '<b>Dedicated</b> customer success manager',
      white_label: '<b>Whitelabel</b> options',
      personalised: '<b>Personalised</b> project sourcing',
      custom: '<b>Custom</b> SLA',
    },
  },

  FeaturesTable: {
    starter: 'Starter',
    growth: 'Growth',
    premium: 'Premium',
    basic: 'Basic',
    advanced: 'Advanced',
    full: 'Full',
    features: 'Features',
    dashboard: 'Private dashboard',
    team_offset: 'Offset your team',
    tooltip_team_offset:
      'Add monthly subscriptions to offset your team members carbon & plastic footprints. Create your Earth Positive workforce and attract the best talent.',
    manual_impact: 'Add manual impact',
    tooltip_manual_impact:
      'Easily plant trees, rescue plastic or offset CO2 from within your dashboard.',
    automatic_trees: 'Trees automatically planted',
    tooltip_automatic_trees:
      'Each month we plant extra trees on your behalf. These will appear in your dashboard automatically.',
    engagement_hub: 'Engagement hub',
    public_profile: 'Public Profile',
    tooltip_public_profile:
      'Share your impact with your community with a personalised public profile.',
    badges_widgets: 'Badges & Widgets',
    tooltip_badges_widgets:
      'Showcase your impact across your site and throughout the full customer journey with dynamic widgets that incentivise action.',
    impact_wallet: 'Customer Impact Wallet',
    tooltip_impact_wallet:
      'Bring your customers in on your impact story. Give them the ability to track their impact within a unique profile, send automated certificates, project and campaign updates and connect with them on a whole new level.',
    qr_code: 'QR Code',
    tooltip_qr_code:
      'Your QR code links to your public profile. Use it on swing tags, flyers, business cards, or in-store merchandising to bridge the gap between the digital and physical elements of your impact story.',
    integrations_automations: 'Integrations & Automations',
    active_integrations: 'Active integrations',
    tooltip_active_integrations:
      'Set your impact on autopilot by  integrating your shop, email marketing and reviews platform.',
    basic_automations: 'Basic automations',
    tooltip_basic_automations:
      'Basic automations include all newsletter and reviews triggers as well as impact per order for e-commerce integrations.',
    advanced_automations: 'Advanced automations',
    tooltip_advanced_automations: 'Advanced automations include all of our available triggers.',
    network: 'Network',
    refer_partners: 'Refer partners',
    tooltip_refer_partners:
      'Give and receive environmental impact with every partner you successfully refer.',
    reward_partners: 'Reward partners',
    tooltip_reward_partners: 'Reward your chosen partners with selected impact every month.',
    analytics: 'Analytics',
    basic_analytics: 'Basic analytics',
    tooltip_basic_analytics:
      'Gain a better understanding of the impact you’ve made and what that means for your business.',
    advanced_analytics: 'Advanced analytics',
    tooltip_advanced_analytics:
      'Deep dive into your impact analytics. Learn more about your impact and how your community is engaging with your environmental mission. ',
    widget_api: 'Widget API',
    tooltip_widget_api:
      'Easily display our widgets and badges anywhere or create your own. The Widget API provides you with full design flexibility.',
    greenspark_api: 'Greenspark API',
    tooltip_greenspark_api:
      'Use the Greenspark API to seamlessly integrate impact into any business use case.',
    support: 'Support',
    chat_email: 'Chat & email support',
    technical: 'Technical support',
    campaign_support: 'Priority campaign support',
    tooltip_campaign_support:
      'Receive 1-2-1 support from our marketing team to help you successfully run impact based campaigns.',
    impact_pricing: 'Impact Pricing',
    starter_plan_trees: '1 / month',
    growth_plan_trees: '15 / month',
    premium_plan_trees: '30 / month',
    unlimited: 'Unlimited',
    media_pack: 'Media pack',
    developer_success: 'Developer success',
    questions: 'Have more questions? {0} or {1}!',
    get_in_touch: 'Get in touch',
    book_demo: 'Book a Demo',
    users: 'Users',
    users_tooltip:
      'Collaborate with your team and manage who has access to your Greenspark account.',
  },

  CheckoutItem: {
    starter_business: 'Monthly Starter',
    starter_business_yearly: 'Yearly Starter',
    growth_business: 'Monthly Growth',
    growth_business_yearly: 'Yearly Growth',
    premium_business: 'Monthly Premium',
    premium_business_yearly: 'Yearly Premium',
    free_business_trial: 'Free Business (14 day free trial)',
    starter_business_trial: 'Monthly Starter (14 day free trial)',
    starter_business_yearly_trial: 'Yearly Starter (14 day free trial)',
    growth_business_trial: 'Monthly Growth (14 day free trial)',
    growth_business_yearly_trial: 'Yearly Growth (14 day free trial)',
    premium_business_trial: 'Monthly Premium (14 day free trial)',
    premium_business_yearly_trial: 'Yearly Premium (14 day free trial)',
    earth_hero: 'Earth Hero',
    plastic_hero_personal: 'Plastic Hero',
    free_business: 'Free Business',
    personal: 'Personal',
  },

  CallToUpgradePopup: {
    call_to_upgrade_title: 'Upgrade to use this feature',
    call_to_subscribe_title: 'Subscribe to a plan',
    plans_text1:
      'This feature is available on <span>Starter</span>, <span>Growth</span> and <span>Premium</span> plans.',
    plans_text2: 'This feature is available on <span>Growth</span> and <span>Premium</span> plans.',
    plans_text3: 'This feature is only available with a <span>Premium</span> plan.',
    upgrade_text: 'You are currently on a <span>{currentPlan}</span> plan.',
    upgrade_text_partner:
      'Your partner has subscribed you to a <span>{currentPlan}</span> plan, please either contact your partner for an upgrade or subscribe to a plan.',
    select_plan_text_partner:
      'Your partner has not paid a subscription for you, please either contact your partner or subscribe to a plan.',
    upgrade: 'upgrade',
    subscribe: 'select a plan',
    domain_text:
      '<p>On a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} custom domain</span>.</p><p>Upgrade to a <span>{nextPlan}</span> plan to gain access to <span> {nextPlanMaxAmount} custom domains </span></p>',
    integration_text:
      '<p>On a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} active integration</span>.</p><p>Upgrade to a <span>Growth</span> or <span>Premium</span> plan to gain access to <span>{nextPlanMaxAmount} integrations</span> or more.</p>',
    integration_text_premium:
      '<p>On a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} active integration</span>.</p><p>Upgrade to a <span>{nextPlan}</span> plan to gain access to <span> {nextPlanMaxAmount} integrations </span></p>',
    integration_text_generic:
      'On your current plan plan you can have up to {maxAmount} active integration. Upgrade your plan to gain access to further integrations.',
    trigger_text:
      '<p>On a <span>{currentPlan}</span> plan you can only use <span>basic triggers</span></p><p>Upgrade to <span>Growth</span> or <span>Premium</span> plan to use <span> advanced triggers</span></p>',
    domain_text_partner:
      '<p>Your partner has subscribed you to a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} custom domain</span></p><p>Upgrade to a <span>{nextPlan}</span> plan to gain access to <span> {nextPlanMaxAmount} custom domains </span></p>',
    template_text:
      '<p>On a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} custom template</span></p><p> Upgrade to a <span>{nextPlan}</span> plan to gain access to <span> {nextPlanMaxAmount} custom templates </span></p>',
    template_text_partner:
      '<p>Your partner has subscribed you to a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} custom template</span></p><p> Upgrade to a <span>{nextPlan}</span> plan to gain access to  <span> {nextPlanMaxAmount} custom templates </span></p>',
    team_text:
      '<p>On a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} users</span>.</p><p>Upgrade to a <span>{nextPlan}</span> plan to add <span> {nextPlanMaxAmount} users</span></p>',
    team_text_partner:
      '<p>Your partner has subscribed you to a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount} users</span></p><p>Upgrade to a <span>{nextPlan}</span> plan to gain access to <span> {nextPlanMaxAmount} team users</span></p>',
    manage: 'manage automations',
    integrator_text:
      '<p>You already have an active integration with <span>{integrator}</span>. Add more automations for this integration</p>',
    integrator_title: 'Add more automations',
  },

  ChangePlanPopup: {
    upgrade_title: 'You’re upgrading your plan!',
    downgrade_title: 'You’re downgrading your plan!',
    upgrade: {
      subtitle:
        'You’re about to upgrade from a <span>{currentPlan}</span> plan to a <span>{selectedPlan}</span> paid plan.',
      disclaimer:
        'This will cost {totalAmount} and the <strong>payment will be charged on your default payment method at the end of the billing cycle.</strong>',
    },
    upgrade_from_trial: {
      subtitle:
        'You’re about to upgrade from a free trial on a <span>{currentPlan}</span> plan to a <span>{selectedPlan}</span> paid plan.',
      disclaimer_per_month:
        'This will cost {totalAmount} per month and the <strong>payment will be charged on your default payment method immediately.</strong>',
      disclaimer_per_year:
        'This will cost {totalAmount} per year and the <strong>payment will be charged on your default payment method immediately.</strong>',
    },
    downgrade: {
      subtitle:
        'You’re about to downgrade from a <span>{currentPlan}</span> plan to a <span>{selectedPlan}</span> paid plan.',
      disclaimer:
        'This will cost {totalAmount} and the <strong>payment will be charged on your default payment method at the end of the billing cycle.</strong>',
    },
    downgrade_from_trial: {
      subtitle:
        'You’re about to downgrade from a free trial on a <span>{currentPlan}</span> plan to a <span>{selectedPlan}</span> paid plan.',
      disclaimer_per_month:
        'This will cost {totalAmount} per month and the <strong>payment will be charged on your default payment method immediately.</strong>',
      disclaimer_per_year:
        'This will cost {totalAmount} per year and the <strong>payment will be charged on your default payment method immediately.</strong>',
    },
    plan: 'Plan',
    customer_engagement_portal:
      'Please turn off your post purchase engagement in order to downgrade your plan',
    integrations:
      'Currently you have {currentAmount} integrations active, but your selected plan only supports {maxAmount}. Please turn off as many as needed to downgrade!',
    custom_domains:
      'Currently you have {currentAmount} custom domains, but your selected plan only supports {maxAmount}. Please deactivate as many as needed to downgrade!',
    custom_email_templates:
      'Currently you have {currentAmount} of custom email templates active, but your selected plan only supports {maxAmount}. Please turn off as many as needed to downgrade!',
    partners:
      'Currently you have active recurring payments for your partners. Please turn them off to downgrade!',
    upgrade_button_text: 'Upgrade my plan',
    upgrade_from_trial_button_text: 'End trial and upgrade my plan',
    downgrade_button_text: 'Downgrade my plan',
    downgrade_from_trial_button_text: 'End trial and downgrade my plan',
    cancel_button_text: 'cancel',
    terms: 'Terms & Conditions',
    plan_updated: 'Plan successfully updated',
    error: 'Something went wrong, please try again',
    change_plan_error:
      'An error occurred while changing your plan due to an insufficient spending limit. Please try again.',
    privacy: 'Privacy Policy',
    accept: 'Accept our {0} & {1}',
    step_two_title: 'Set new custom spending limit for {store}',
    step_two_upgrade_description:
      '<strong>To upgrade to the {selectedPlan} plan, set a new spending limit that suits your subscription and business needs. A higher limit helps you avoid hitting your spending cap early in the month, ensuring uninterrupted impact creation and access to all features.</strong><br />All billing will be in $USD. You can track and adjust your spending anytime from your account dashboard.',
    step_two_downgrade_description:
      '<strong>To downgrade to the {selectedPlan} plan, set a new spending limit that suits your subscription and business needs. A higher limit helps you avoid hitting your spending cap early in the month, ensuring uninterrupted impact creation and access to all features.</strong><br />All billing will be in $USD. You can track and adjust your spending anytime from your account dashboard.',
    step_three_title: 'Confirmation needed',
    step_three_description:
      'To confirm your spending limit for {store}, <strong>we’ll redirect you to Shopify to review and approve</strong> the updated charges.<br /><br />Once approved, you’ll return here, and your <strong>new spending limit will be active!</strong>',

    step_three_success_title: 'Your new plan and limit is up and running!',
    step_three_success_description:
      'Your account has been successfully updated to the <strong>{selectedPlan} plan</strong> with a spending limit of <strong>${limit}</strong>.<br />Thank you!',
    cancel_upgrade_alert_title: 'Are you sure you want to cancel this upgrade?',
    cancel_upgrade_alert_description:
      'If you stop now, your account <strong>will stay on the {currentPlan} plan.</strong> Upgrading to {selectedPlan} plan later will require <strong>restarting the process from the beginning.</strong>',
    cancel_upgrade: 'Cancel upgrade',
    continue_upgrade: 'Proceed with upgrade',
  },

  MaintenanceView: {
    title: 'Maintenance Mode',
    text: 'We are working on a major update. Everything will be back online at {date}',
    home_link_text: 'GO TO HOMEPAGE',
    contact_link_text: 'Contact us',
  },

  CustomDomain: {
    custom_domain_description:
      'Ensure higher deliverability and open rates by sending your customer emails from your own domain.',
    custom_domain_docs: 'Click here to read more about adding a custom domain.',
    email_domains: 'Email domains',
    new_domain: 'New domain',
    new_domain_hint: 'New domain eg. example.com',
    from_domain: 'From domain',
    domain_hint: 'Enter the domain you would like emails to be sent from.',
    domain_tooltip:
      'In order to send out notifications using your own domain, assign it to the relevant notification templates. This can only be done after successful domain verification.',
    dns_setting: 'DNS setting',
    dns_hint: 'Add all of the records below to your host’s DNS section.',
    verify: 'Verify',
    verified: 'verified',
    verify_hint: 'Once you have added all of the records press verify.',
    verify_label: 'I’ve added all records',
    verify_again_label: 'Verify domain again',
    domain_verified: 'Your domain is successfully verified',
    next: 'Next',
  },

  DeleteDomainDialog: {
    delete_text:
      'By deleting the {0} custom domain it will be lost for ever. Any email templates that use this domain should be updated.',
    delete_text_template:
      'By deleting the {0} email template it will be lost for ever and you will no longer be ale to use the template.',
    approve_delete: 'I want to delete this custom domain',
    approve_delete_template: 'I want to delete this email template',
    delete_domain: 'Delete domain',
    delete_template: 'Delete template',
    domain_deleted: 'Domain successfully deleted',
    template_deleted: 'Template successfully deleted!',
    error: 'An error occurred, please try again or get in touch and we would be happy to help.',
    cancel: 'Cancel',
  },

  NotificationTemplates: {
    template_description:
      'Manage the email notifications sent to your customers. Use our pre-defined templates or create your own. Use the variables provided to add dynamic data throughout your emails.',
    header_title: 'Email templates',
    nudge: 'Nudge',
    nudge_desc:
      'Nudges are sent out on the 3rd, 7th and 14th day after the customer’s first impact action took place.',
    nudge_schedule_checkbox: 'Customise the schedule of nudge e-mails!',
    invitation: 'Invitation',
    invitation_desc:
      'Sent when a customer has not yet claimed their account and completes an impact action such as placing an order.',
    update: 'Update',
    update_desc:
      'Sent when a customer has claimed their account and completes an impact action such as leaving a review.',
    activate: 'Activate',
    activate_hint: 'Start sending this template to your customers.',
    activate_warning:
      'Activating this template will automatically deactivate any active two-way data sync in your account.',
    activate_error:
      'In order to activate your template, you need to setup a valid custom domain first.',
    type: 'Type',
    template_name: 'Template name',
    template_name_hint:
      'Enter a name to help you remember what this template is all about. Only you will see this.',
    template_type: 'Template type',
    all: 'All',
    offset_per_order: 'Per order',
    offset_per_product: 'Per product',
    offset_by_store_revenue: 'Spend level',
    offset_by_spend: 'Tiered spend level',
    by_percentage_of_revenue: 'Percentage of total revenue',
    per_paid_invoice: 'Paid invoice',
    by_percentage_of_paid_invoice: 'Percentage of invoice',
    by_spend_of_paid_invoice: 'Spend level of invoice',
    by_tiered_spend_of_paid_invoice: 'Tiered spend level of invoice',
    offset_per_review: 'Per review',
    offset_per_subscription: 'Per newsletter subscriber',
    offset_by_percentage_order: '% of order',
    by_loyalty_points: 'Loyalty points',
    offset_by_api: 'API',
    per_completed_form: 'Per completed form',
    per_selected_answer: 'Per selected answer',
    trigger: 'Trigger',
    trigger_hint: 'Choose the impact action which triggers your email to be sent out.',
    trigger_checkbox: 'Customise triggers',
    settings: 'Settings',
    from: 'From',
    from_hint: 'This is the first thing a customer sees in their inbox.',
    custom_sender_email_checkbox: 'Custom sender email',
    alias: 'Alias',
    alias_hint: 'This is the first thing a customer sees in their inbox.',
    alias_hint_bold: ' By default we use: {default}',
    custom_alias_checkbox: 'Custom alias',
    subject: 'Subject',
    subject_hint:
      'Your subject line is one of the first things that your customers read and is a key driver in successful open rates.',
    subject_hint_bold: 'By default we use: {default}.',
    custom_subject_checkbox: 'Custom subject',
    pre_header: 'Preheader',
    pre_header_hint:
      'The preheader is the text following the subject line when an email is previewed. It is one of the first three items a customer sees when receiving your email.',
    pre_header_hint_bold: 'By default we use: {default}.',
    custom_pre_header_checkbox: 'Custom preheader',
    schedule: 'Schedule',
    schedule_hint:
      'You can choose to send your template continuously starting today or only during a specific date range. (Dates are set in UTC timezone).',
    start_date: 'Start date',
    end_date: 'End date',
    end_date_checkbox: 'Set an end date',
    content: 'Content',
    header_image: 'Header image',
    header_image_hint:
      'Add your own logo to the top of the email or use our pre-defined header image.',
    header_image_hint_bold:
      'If you use your own header image please ensure it is maximum 1370 x 700, or has a 2:1 aspect ratio.',
    header_image_checkbox: 'Your logo',
    body: 'Body',
    body_hint:
      'Use our pre-defined email body or customise it to fit your brand. Add dynamic data by using the variables provided.',
    custom_body_checkbox: 'Custom email body',
    overview: 'Overview',
    overview_hint: 'Show or hide our email footer explaining the benefits of Greenspark.',
    hide_overview_checkbox: 'Hide Greenspark overview',
    total_impact: 'Hide total impact',
    total_impact_hint: 'Display your total impact so far within your emails.',
    hide_total_impact: 'Hide total impact',
    test: 'Test',
    send_test: 'Send a test email',
    send_test_hint: 'Test your email before sending to your recipients.',
    send: 'Send',
    nudge_schedule: 'Nudge schedule',
    nudge_schedule_hint:
      'Choose when your nudge email is sent out after a customer completes an impact action.',
    schedule_days: '{days} days',
    save: 'Save',
    cancel: 'Cancel',
    email_sent: 'Test email has successfully sent!',
    template_saved: 'Template successfully saved!',
    template_deleted: 'Template deleted!',
    error: 'An error occurred, please try again or get in touch and we would be happy to help.',
    new_template: 'New template',
    no_domain_text:
      'Looks like you have no custom domains yet. You are able to set up custom domains {0}',
    domain_invalid:
      'The custom domain you’ve selected is not valid. Make sure to select a valid one. Or you can validate it {0}.',
    link_here: 'here',
    schedule_error: 'Please select an end date that is subsequent to the starting date!',
    required: 'This field is required',
    form_error_test: 'Sending a test e-mail is disabled, as long as an error occurs!',
    form_error_save: 'Saving a template is disabled, as long as an error occurs!',
    body_length_exceeded: 'Invalid character length, please insert {limit} characters or less!',
  },
  EmailPreview: {
    text: 'This is the message your customer sees in their inbox.',
  },
  VariablesPreview: {
    text: 'Use the dynamic variables provided to customise your email.',
    variable: 'Variable',
    example: 'Example',
  },
  ReferenceBox: {
    title: 'join hundreds who automate their impact using greenspark',
    increase_title: 'INCREASE 1.8x',
    increase_text: 'Conversion Rate',
    rise_title: '133% RISE ',
    rise_text: 'User Reviews',
    more_title: '64% MORE',
    more_text: 'Customer Engagement',
    uplift_title: '2.7x UPLIFT',
    uplift_text: 'Campaign Revenue',
  },
  RulesMixin: {
    max_length: 'Invalid character length, please insert {limit} characters or less!',
    min_length: 'Invalid character length, please insert minimum {limit} characters or more!',
    required: 'Required!',
    email: 'Please provide a valid e-mail address!',
    url: 'Please provide a valid URL!',
    max_value: 'Please provide a value less than or equal to {limit}!',
    min_value: 'Please provide a value more than or equal to {limit}!',
    array_min_length: 'Please select at least {limit} items!',
  },
  TreePlanterOverlay: {
    title: 'Give us a moment while we plant your <span>first tree...</span>',
  },
  SelectIntegration: {
    placeholder: 'Which integration do you want to start with?',
    store: 'Ecommerce',
    review: 'Review',
    email: 'E-mail',
    survey: 'Survey',
    integrator: 'Integrator',
    billing: 'Payments',
    loyalty: 'Loyalty',
    custom: 'Custom',
    custom_integration: 'Create custom integration',
    all: 'All',
    no_result: 'There are no integrations available for "{searchValue}"',
    help: 'Need help finding your credentials?',
    active: 'Active',
    form: 'Form',
    coming_soon: 'Coming soon',
  },
  SelectTrigger: {
    integration: 'Integration',
    name_automation: 'Name your automation',
    automation_name: 'Automation name',
    choose_trigger: 'Choose your trigger',
    offset_per_order: 'Per order impact',
    description_offset_per_order:
      'Automatically add impact to each order in your store. Customers will create one impact no matter how many items they buy at one time.',
    offset_per_product: 'Per product impact (customised)',
    description_offset_per_product:
      'Customise your per product settings so that specific products create specific impacts. Easily tailor individual or groups of products.',
    offset_all_products: 'Per product (all products)',
    description_offset_all_products:
      'Create impact for every single product you sell. The same impact settings will be applied to all your products at once.',
    offset_by_spend: 'Spend threshold',
    description_offset_by_spend:
      'Incentivise your customers to spend more by adding spend related impact. Works exactly the same as a free shipping threshold with added impact!',
    offset_by_store_revenue: 'Tiered spend level',
    description_offset_by_store_revenue:
      'Create impact for every X amount spent per order on a tiered basis (i.e 3x spend = 3x impact). Maximise your impact and reward customers.',
    offset_by_percentage_order: '% of order impact',
    description_offset_by_percentage_order:
      'Dedicate a percentage of the order total to your chosen impact. We will always round down to ensure the threshold is never exceeded.',
    automation_tooltip:
      'Pick a clear and memorable name for your automation - your customers will not see it and you can change anytime.',
    per_subscriber: 'Impact per email subscriber',
    description_per_subscriber:
      'Grow your email list by automatically adding impact for every new newsletter subscriber.',
    per_sms_subscriber: 'Impact per SMS subscriber',
    description_per_sms_subscriber:
      'Take your SMS lists to new heights by automatically rewarding new subscribers with impact.',
    per_product_review: 'Impact per product review',
    description_per_product_review:
      'Have an impact for every new product review left by your customers. Multiple products in an order means more impact for our planet.',
    per_company_review: 'Impact per company review',
    description_per_company_review:
      'Automatically add impact for every new company review. Customers will create one impact for each company review they leave.',
    by_loyalty_points: 'Loyalty Points',
    description_by_loyalty_points:
      'Let your customers use their loyalty points to make a positive impact. Choose which kind of environmental initiatives they can support.',
    notification_text: 'Not available on {planName}',
    per_completed_form: 'Per completed form',
    description_per_completed_form:
      'Automatically add impact to each form that your customers fill out.',
    per_selected_answer: 'Per selected answer',
    description_per_selected_answer:
      'Let for respondents choose which impact they want to have for each completed form.',
    per_paid_invoice: 'Per invoice',
    description_per_paid_invoice:
      'Automatically creates your chosen environmental impact per invoice paid, regardless of the invoiced amount.',
    by_percentage_of_paid_invoice: '% of invoice value',
    description_by_percentage_of_paid_invoice:
      'Dedicate a specific percentage of the invoice value to your chosen impact. Always rounded down to ensure the threshold is never exceeded.',
    by_spend_of_paid_invoice: 'Spend threshold',
    description_by_spend_of_paid_invoice:
      'Incentivise your customers to spend more by adding spend related impact. Works exactly the same as a free shipping threshold with added impact!',
    by_tiered_spend_of_paid_invoice: 'Tiered spend level',
    description_by_tiered_spend_of_paid_invoice:
      'Create impact for every X amount spent on a tiered basis (i.e 3 x spend = 3 x impact). Maximise your impact and reward customers.',
    by_percentage_of_revenue: '% of total revenue',
    description_by_percentage_of_revenue:
      'Dedicate a percentage of your total revenue per month to create positive impact. ',
  },
  SelectProjects: {
    integration: 'Integration',
    automation: 'Name your automation',
    trigger: 'Choose your trigger',
    impact_per_question_title: 'Setup your impact per question',
    impact_per_question_description:
      'Select which impact you want to generate with the corresponding answers.',
    schedule: 'Schedule',
    schedule_tooltip:
      'Start your trigger continuously from today or choose a specific date range during which it will be active.',
    all_product: 'Set impact for all products',
    zero_value_trigger_disclaimer_part_1:
      'This automation will trigger on all invoices from {currency_symbol}0 and up. Use the',
    spend_threshold_trigger: 'spend threshold trigger',
    zero_value_trigger_disclaimer_part_2: 'to set up a minimum invoice value.',
    by_percentage_of_revenue_disclaimer:
      'Please note that the % amount you choose <strong>may not create a full unit of your chosen impact</strong> for each invoice. <strong>Only full units of impact</strong> are synced with your marketing suite and/or post purchase messaging at an invoice level.',
    per_subscriber: {
      title: 'Impact per email subscriber',
      text: '{amount} per e-mail subscriber',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per subscriber: {price}',
      total_paid_plan: 'Total cost per subscriber if you subscribe to a paid plan: {price}',
    },
    per_sms_subscriber: {
      title: 'Impact per SMS subscriber',
      text: '{amount} per SMS subscriber',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per subscriber: {price}',
      total_paid_plan: 'Total cost per subscriber if you subscribe to a paid plan: {price}',
    },
    per_product_review: {
      title: 'Impact per product review',
      text: '{amount} per product review',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per product review: {price}',
      total_paid_plan: 'Total cost per product review if you subscribe to a paid plan: {price}',
    },
    per_company_review: {
      title: 'Impact per company review',
      text: '{amount} per company review',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per company review: {price}',
      total_paid_plan: 'Total cost per company review if you subscribe to a paid plan: {price}',
    },
    per_completed_form: {
      title: 'Per completed form',
      text: '{amount} per completed form',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per completed form: {price}',
      total_paid_plan: 'Total cost per completed form if you subscribe to a paid plan: {price}',
      all: 'All',
    },
    per_selected_answer: {
      title: 'Per selected answer',
      text: '{amount} per selected answer',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per question: {price}',
      total_paid_plan: 'Total cost per selected answer if you subscribe to a paid plan: {price}',
      all: 'All',
    },
    offset_per_order: {
      title: 'Per order impact',
      text: '{amount} per order',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per order: {price}',
      total_paid_plan: 'Total cost per order if you subscribe to a paid plan: {price}',
    },
    offset_per_product: {
      title: 'Per product (customised)',
      text: '{amount} per product',
      impact_header_title: 'Choose your impact per product sold',
      impact_header_description:
        'Use the filters below to choose specific products and customise their individual impact. Tailor your setup for single or groups of products.',
      total: 'Total cost per product: {price}',
      total_paid_plan: 'Total cost per product if you subscribe to a paid plan: {price}',
    },
    offset_all_products: {
      title: 'Per product (all products)',
      text: '{amount} per product',
      impact_header_title: 'Choose your impact for all your products',
      impact_header_description:
        'Choose which impact projects you want to support for each product sold. This setting will be applied to all your products at once.',
      total: 'Total cost per product: {price}',
      total_paid_plan: 'Total cost per product if you subscribe to a paid plan: {price}',
    },
    offset_by_spend: {
      title: 'Spend threshold',
      text: '{amount} per {type} order',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      total: 'Total cost per {spend_level} order: {price}',
      total_paid_plan:
        'Total cost per {spend_level} order if you subscribe to a paid plan: {price}',
      label: 'Basket / order value',
      tooltip:
        'Select the currency and total amount a customer should spend in your store in order to activate the impact trigger.',
    },
    offset_by_store_revenue: {
      title: 'Tiered spend level',
      text: '{amount} per {type} spent in your store',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      label: 'Spend threshold',
      tooltip:
        'Select the currency and spend threshold for a customer to activate the impact trigger (e.g. 3x spend = 3x impact).',
      total: 'Total cost per {spend_level} spent in your store: {price}',
      total_paid_plan:
        'Total cost per {spend_level} spent in your store if you subscribe to a paid plan: {price}',
    },
    offset_by_percentage_order: {
      title: '% of order impact',
      text: '{type} of the order will go to {amount}',
      impact_header_title: 'Choose your impact',
      impact_header_description:
        'Choose which impact project you want to support. You will be billed by the amount of impact units you select.',
      label: 'Order percentage',
      tooltip:
        'Enter the percentage of the order value you want to automatically dedicate to your chosen impact.',
    },
    per_paid_invoice: {
      title: 'Per invoice',
      text: '{amount} per paid invoice',
      impact_header_title: 'Choose your impact per invoice paid',
      impact_header_description:
        'Choose which impact projects you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      total: 'Total cost per invoice: {price}',
      total_paid_plan: 'Total cost per invoice if you subscribe to a paid plan: {price}',
    },
    by_percentage_of_paid_invoice: {
      title: '% of invoice value',
      text: '{type} of the invoice value will go to {amount}',
      impact_header_title: 'Select your impact per percentage of invoice value',
      impact_header_description:
        'Choose which impact project you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      label: 'Invoice percentage',
      tooltip:
        'Enter the percentage of the invoice value you want to automatically dedicate to your chosen impact.',
    },
    by_spend_of_paid_invoice: {
      title: 'Spend threshold',
      text: '{amount} per invoce that is over {type}',
      impact_header_title: 'Choose your impact per spend threshold',
      impact_header_description:
        'Choose which impact projects you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      label: 'Invoice value',
      tooltip: 'Select the invoice currency and total needed to trigger the automation.',
      total: 'Total cost per {spend_level} spent: {price}',
      total_paid_plan:
        'Total cost per {spend_level} spent if you subscribe to a paid plan: {price}',
    },
    by_tiered_spend_of_paid_invoice: {
      title: 'Tiered spend level',
      text: '{amount} per {type} spent',
      impact_header_title: 'Choose your impact per tiered spend level',
      impact_header_description:
        'Choose which impact projects you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      label: 'Tiered spend level',
      tooltip:
        'Select the currency and tiered spend level for a customer to activate the impact trigger (e.g. 3x spend = 3x impact).',
      total: 'Total cost per {spend_level} spent: {price}',
      total_paid_plan: 'Total cost per invoice if you subscribe to a paid plan: {price}',
    },
    by_percentage_of_revenue: {
      title: '% of total revenue',
      text: '{type} of the invoice value will go to {amount}',
      impact_header_title: 'Select your impact per percentage of total revenue',
      impact_header_description: 'Choose which impact projects you want to support.',
      label: 'Percentage of revenue',
      tooltip:
        'Enter the percentage of your total revenue per month that you want to automatically dedicate to your chosen impact.',
    },
    by_loyalty_points: {
      title: 'Loyalty Points',
      text: '{amount} per redemption',
      impact_header_title: 'Set up impact per rule',
      impact_header_description:
        'Based on your Stamped point redemption rules, select which impact you want to generate for each Rule ID via Greenspark.',
      trees: '{count} tree | {count} trees',
      carbon: '{count} kg of CO2 | {count} kgs of CO2',
      plastic: '{count} plastic bottle | {count} plastic bottles',
      kelp: '{count} kelp',
      water: '{count} day of water | {count} days of water',
      bees: '{count} x 20 bees | {count} x 20 bees',
      total: 'Total cost per redemption: {price}',
      total_paid_plan: 'Total cost per redemption if you subscribe to a paid plan: {price}',
    },
    save: 'Save',
    min_limit: 'minimum {limit}',
  },
  OnboardingView: {
    select_integration_title: 'Welcome to Greenspark!',
    select_integration_description:
      'You’re in! Now it’s time to add your impact. Start by creating your first integration.',
    select_trigger_title: 'Great! Now let’s add your first automation',
    select_trigger_description: {
      store:
        'Choose the triggers that allow your customers to create positive impact. Whether it is planting a tree for every order or rescuing plastic for each product sold.',
      email:
        'Choose the triggers that allow your customers to create positive impact. Whether it is planting a tree or rescuing plastic for each new email subscriber.',
      feedback:
        'Choose the triggers that allow your customers to create positive impact. Whether it is planting a tree for every company review, rescuing plastic for each product review or both!',
      review:
        'Choose the triggers that allow your customers to create positive impact. Whether it is planting a tree for every company review, rescuing plastic for each product review or both!',
      form: 'Choose the triggers that allow your customers to create positive impact. Whether it is planting a tree or rescuing plastic for each form completed.',
      billing:
        'Select a trigger to automate your impact strategy by choosing which customer activity creates positive climate action.',
    },
    select_project_title: 'Choose impact',
    select_project_description:
      'Automatically support your chosen projects to create positive climate action.',
    custom_integration_title: 'Great! Let’s setup your custom integration',
    custom_integration_description:
      'With our public API you can connect Greenspark to any other app or fully embed the experience in your product. Learn more by reading our  <a href="https://greenspark.readme.io" target="_blank">API documentation.</a>',
    create_api_key_title: 'Create your API key',
    create_api_key_description:
      'Now that you have a source and trigger to identify your API calls, let’s create an API key that you can use to access with the Greenspark API.  Learn more with our <a href="https://greenspark.readme.io" target="_blank">API documentation.</a>',
    api_key_custom: 'Your API Key is ready!',
    zapier_integration_title: 'Great! Let’s setup your Zapier integration',
    integrately_integration_title: 'Great! Let’s setup your Integrately integration',
    zapier_integration_description: `With our Zapier integration API you can connect Greenspark to 5000+ apps. Learn more by reading our  <a href="${ZAPIER_DOCUMENTATION_URL}" target="_blank">Zapier integration documentation.</a>`,
    integrately_integration_description: `With our Integrately integration API you can connect Greenspark to 1100+ apps. Learn more by reading our  <a href="${INTEGRATELY_DOCUMENTATION_URL}" target="_blank">Integrately integration documentation.</a>`,
    create_custom_api_key_title: 'Create your {source} key',
    create_custom_integration_key_description:
      'Create your {source} API key in Greenspark and use it to authenticate your {source} integration. Learn more: <a href="{documentationUrl}" target="_blank">{source} documentation.</a>',
    copy_api_key_title: 'Copy your {source} key',
    api_key_integrately: 'Your Integrately Key is ready!',
    invite_users_title: 'Invite others to your team',
    invite_users_description:
      'You\'re subscribed to the <b>{plan} Plan</b>. You can invite up to <b>{maxTeamMemberCount}</b> coworkers to collaborate with you. Enter their email addresses below and click "Send Invitations" to invite them.',
    skip: 'Skip',
    skip_integration: 'Skip Integration',
    skip_automation: 'Skip Automation',
    skip_invites: 'Skip Invites',
  },
  IntegrationsMixin: {
    help: 'Need help finding your credentials?',
  },
  ShopifyIntegrationAlert: {
    shopify_header: 'Connect your Greenspark account with Shopify',
    shopify_text:
      'When you connect Greenspark with your Shopify store, <strong>your billing will be managed through Shopify</strong>, and <strong>your charges will automatically convert to $USD</strong>. This is a requirement for all Shopify apps. Your updated pricing in $USD is shown below. This is for transparency and information only, <strong>you will not be billed at this time.</strong>',
    shopify_text_dollar:
      'When you connect Greenspark with your Shopify store, <strong>your billing will be managed through Shopify</strong>. This is a requirement for all Shopify apps, we appreciate your understanding and apologise for any inconvenience.',
    plan_price: 'Plan price:',
    plan_monthly: '{ price } per month, billed monthly',
    plan_yearly: '{ price } per month, billed yearly',
    continue: 'Agree and continue',
    cancel: 'cancel',
    trial_period: '(after trial period)',
    total: 'Total in USD',
    standard_team_member: 'Standard Team Member',
    superior_team_member: 'Superior Team Member',
    premium_team_member: 'Premium Team Member',
    tree_planters: 'Tree Planters',
    plastic_heros: 'Plastic Heros',
  },
  ImpactSettingCard: {
    select: 'Select',
    selected: 'Selected',
  },
  ImpactSettingCardHeader: {
    read_more: 'Read more',
    multiple_locations: 'Multiple locations',
  },
  UpcomingEvents: {
    title: 'Upcoming Events',
    see_more: 'See more events',
  },
  YearlyCalendar: {
    title: 'Sustainable Events',
    subtitle: 'Use these events to showcase your impact achievements to your customers',
    useful_resources: 'Useful Resources',
    download_media: 'Download media pack and share your impact',
    boost_engagement: 'How campaigns can boost customer engagement and more',
    active_automations: 'Check your active automations',
    next_event: 'Next event!',
  },
  CustomIntegration: {
    integration: 'Integration',
    custom: 'custom',
    zapier: 'Zapier',
    integrately: 'Integrately',
    source_label: 'Name your integration source',
    source_help:
      'First let’s give your new integration a name. This is only used internally and will never be seen by your customers. ',
    source_name: 'Source name',
    source_tooltip:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Custom Integration. For example: you can store the connected application name as a source name.',
    trigger_label: 'Name your integration Trigger',
    trigger_help_custom:
      'Use triggers to easily manage all of your API calls and ensure they are classified correctly in your analytics. ',
    trigger_help:
      'Use triggers to easily manage all of your different {source} integrations and ensure they are classified correctly in your analytics. ',
    trigger_name: 'Trigger name',
    trigger_tooltip:
      'A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Don’t worry, you can always add more triggers later.',
    trigger_type_label: 'Choose your first trigger type',
    trigger_type_help:
      'Use the trigger type to differentiate your calls, for example, you might want to have one for transactions and one for subscriptions. ',
    trigger_type: 'Trigger type',
    next: 'Next',
    icon_label: 'Upload integration icon (optional)',
    icon_help: 'Upload an icon that helps you easily identify the integration later.',
    upload_icon: 'Upload icon',
    transaction: 'Transaction',
    subscription: 'Subscription',
    order: 'Order',
    subscriber: 'Subscriber',
    review: 'Review',
    affiliate: 'Affiliate',
    partner: 'Partner',
    form_completion: 'Form completion',
    other: 'Other',
    trigger_created: 'Trigger successfully created!',
  },
  UploadIcon: {
    upload_icon: 'Upload icon',
    edit_icon: 'Edit icon',
  },
  CreateApiKey: {
    next: 'Create key',
    integration: 'Integration',
    source_detail: 'Source details',
    trigger_detail: 'Trigger details',
    apikey_label_custom: 'Name your API key',
    apikey_help_custom:
      'The API key name is only used internally and will never be seen by your customers.',
    apikey_label_zapier: 'Name your Zapier key',
    apikey_label_integrately: 'Name your Integrately key',
    apikey_label_toki: 'Name your Toki key',
    apikey_help_zapier:
      'The Zapier key name is only used internally and will never be seen by your customers.',
    apikey_help_integrately:
      'The Integrately key name is only used internally and will never be seen by your customers.',
    apikey_help_toki:
      'The Toki key name is only used internally and will never be seen by your customers.',
    apikey_name: 'Key name',
    transaction: 'Transaction',
    subscription: 'Subscription',
    order: 'Order',
    subscriber: 'Subscriber',
    review: 'Review',
    affiliate: 'Affiliate',
    partner: 'Partner',
    form_completion: 'Form completion',
    other: 'Other',
  },
  CopyApiKey: {
    integration: 'Integration',
    api_key_name: 'Name your {source} key',
    copy_api_key_label_custom: 'Copy your API key',
    copy_api_key_label: 'Copy your {source} key',
    copy_api_key_help_custom:
      'Copy you API key and use it to authenticate any calls you make. You can manage all of your keys from within your Greenspark Account. ',
    alert_custom:
      'In order to finish the onboarding, make sure you copy the API key, what you can only see once!',
    alert:
      'This key will only be shown to you once. Make sure to copy and paste it into your {source} account.',
    done: 'Done',
  },
  EditSimplifiedCustomIntegration: {
    integration: 'Integration',
    table_title: 'Your {source} API keys',
    table_description: `Use your {source} API keys to connect Greenspark with your {source} account. Learn more: <a href="{documentationUrl}" target="_blank">{source} documentation.</a>`,
    next: 'Finish',
    table_name: 'Name',
    table_type: 'Type',
    table_created: 'Created',
    create_key_name: 'Key name',
    placeholder: 'API Key',
    manage: 'Manage',
    rename: 'Rename',
    done: 'Done',
    regenerate: 'Regenerate API Key',
    edit_key_title: 'Rename your {source} API key',
    edit_key_description:
      'This key name is only used internally and will not be seen by customers.',
    regenerate_key_title: 'Regenerate your {source} API key',
    regenerate_key_description: 'Here’s your {source} API key:',
    alert:
      'This key will only be shown to you once. Make sure to copy and paste it into your {source} account.',
  },
  FloatingOnboardingChecklist: {
    add_integration: 'Add your first integration',
    add_automation: 'Setup your first automation',
    plant_trees: 'Plant your first tree',
    add_widgets: 'Add widgets to your site',
    customer_engagement_portal: 'Setup post-purchase engagement',
    offset_team: 'Offset your team carbon footprint',
    add_referral: 'Refer a business',
    title: 'Getting started with Greenspark',
    dismiss: 'Dismiss checklist',
    invite_user: 'Invite your colleagues',
    free_business_subscription:
      'Save <b>at least 25%</b> on impact if you subscribe to one of our plans. <u>Click here and learn how!</u>',
  },
  TopAlertBar: {
    no_subscription: 'Upgrade your account to increase your positive impact!',
    funding_ended: 'Your funded Greenspark account has expired. Upgrade to retain full access.',
    funding_ending:
      'Your funded Greenspark account is about to expire on {expirationDate}. Upgrade to retain full access.',
    unclaimed_allowance: 'You have unclaimed allowance. Update your impact',
    stopped_at: 'Your funded Greenspark account has expired. Upgrade to retain full access.',
    trial_period:
      'You are on a free trial. Any impact you create during this time will still be billed unless you cancel during your trial period.',
    free_business_subscription:
      'Save <b>at least 25%</b> on impact if you subscribe to one of our plans. <u>Click here and learn how!</u>',
  },
  AddPromoCode: {
    add_promo_code: 'Add promo code',
    add_trees100: 'Success! 100 trees will be added to your account after trial!',
    add_trees50: 'Success! 50 trees will be added to your account after trial!',
    add_plastic200: 'Success! 200 plastic bottles will be added to your account after trial!',
    invalid:
      'There was a problem with your promo code. You may be using an invalid or expired code. Please try again.',
    expired:
      'There was a problem with your promo code. You may be using an invalid or expired code. Please try again.',
    dialog_title: 'Your promo code',
    button_text: 'Apply code',
    input_label: 'Promo code',
  },
  SelectShopifyOrderOrigin: {
    title: 'Select order origin',
    pos: 'POS only',
    web: 'Online only',
    all: 'All',
  },
  Users: {
    title: 'Users',
    description:
      'Everything is more fun together! Collaborate with your team and manage who has access to your Greenspark account.',
    invite_user: 'Invite user',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    status: 'Status',
    admin: 'Admin',
    owner: 'Owner',
    pending: 'Pending',
    accepted: 'Accepted',
    you: 'You',
    delete: 'Delete',
    delete_invitation: 'Delete invitation',
    delete_user: 'Delete user',
    cancel: 'Cancel',
    delete_dialog_title: 'Delete user from your team',
    delete_invitation_description:
      'This invitation for <span>{userEmail}</span> will be deleted, and they will no longer be able to sign up for Greenspark to share this account.',
    delete_user_description:
      'By deleting <span>{userEmail}</span> they will no longer be able to access your Greenspark account.',
    delete_dialog_checkbox_text: 'I want to delete this user from my team',
  },
  InviteUserDialog: {
    invite: 'Invite',
    invite_user: 'Invite user',
    invite_user_description: 'Invite team members using their email address.',
    email: 'Email',
    plan_limit_info_text_1:
      'On a <span>{currentPlan}</span> plan you can have up to <span>{maxAmount}</span>.',
    plan_limit_info_text_2: 'You have {0} remaining. Want to add more? {1}',
    users: '{count} user | {count} users',
    upgrade: 'Upgrade',
  },
  UsersModule: {
    user_already_exists:
      'A user with this email already exists. | A user with one of these emails already exists.',
    user_already_invited:
      'A user with this email is already invited. | A user with one of these emails is already invited.',
  },
  ManageIntegrationsAutomationsView: {
    title: 'Manage integrations & automations',
    description:
      'Directly integrate with your favourite tools to create automated impact per order, product, newsletter subscriber and more. Connect in seconds, sit back and watch your impact grow.',
    add_automation: 'Add automation',
    integrations: 'Integrations',
    automations: 'Automations',
    error: 'Re-authenticate',
  },
  ManageIntegrationsView: {
    title: 'Manage Integrations',
    description:
      'Easily add new integrations or edit existing ones. Editing an integration may affect the corresponding automations.',
    automations: 'automations',
    error: 'Re-authenticate',
    active: 'Active',
    reauth: 'Re-authenticate',
    add_integration_title: 'Create additional integrations',
    add_source_title: 'Create additional sources',
    add_integration_description: 'Add another {platform} integration. Any questions?',
    add_source_description: 'Add another custom source. Any questions?',
    get_in_touch: 'Get in touch',
    add_integration: 'Add integration',
    add_source: 'Add source',
  },
  IntegrationsTable: {
    error: 'Re-authenticate',
    active: 'Active',
    success: 'Active',
    reauth: 'Re-authenticate',
    add_automation: 'Add automation',
    edit: 'Edit',
    pending: 'Pending',
    disabled: 'Disabled',
    automations: '{count} automation | {count} automations',
    triggers: '{count} trigger | {count} triggers',
    delete_integration_dialog_title: 'Delete integration',
    delete_integration_description:
      'By deleting your <span>{integration}</span> integration any automations that use it will stop running.',
    delete_integration: 'Delete integration',
    delete_integration_checkbox_label: 'I want to delete this integration',
    select: 'Select',
  },
  CarbonFootprintView: {
    title: 'Calculate Your Business Carbon Footprint',
    description: 'Now you can easily calculate the carbon footprint of your business.',
  },
  CarbonFootprintIntroduction: {
    title: 'Why?',
    description:
      'The everyday activities of your business contribute to climate change through greenhouse gas emissions. By calculating your business’ carbon footprint, you are taking the first step to protect the environment and create a more sustainable future.',
    reason_1: 'Easily track the scopes 1,2 and 3 emissions of your business.',
    reason_1_tooltip: `
      <p><b>Scope 1 (Direct Emissions):</b> Emissions directly produced by your company.</p>
      <p><b>Scope 2 (Indirect Emissions):</b> Emissions associated with the generation of any energy your company uses (mainly electricity and heat). There are two ways of measuring Scope 2 emissions: Location Based, which takes an average emissions factor for your region, and Market Based, which uses supplier-specific emissions factors.</p>
      <p><b>Scope 3 (Indirect Other Emissions):</b> Emissions generated in your company’s value chain. This calculator focuses on three subcategories of Scope 3, namely 3.1) Purchased Goods and Services, 3.6) Employee Commuting and 3.7) Business Travel. These three subcategories are all upstream emissions sources.</p>`,
    reason_2: 'Prove commitment to emission reduction to customers and stakeholders.',
    reason_3: 'See where to focus your emissions-reduction efforts.',
    reason_4: 'Receive a Greenhouse Gas Protocol compliant report.',
    reason_4_tooltip: `
      <p><b>Greenhouse Gas Protocol:</b></p>
      <p>The calculation follows the Greenhouse Gas protocol (GHG Protocol) official corporate emissions reporting standard. This categorises emissions sources into 3 scopes, so that businesses across different industries can report using a single, unified framework.</p>
      <p>Emissions are quantified in kilograms of carbon dioxide equivalent (CO2e). This is a way of combining all the different greenhouse gases into a single, easy to track metric. The quantities of other gases are converted to CO2e based on their Global Warming Potential values.</p>
      <p>Find more information about the <a target="_blank" href="https://ghgprotocol.org/corporate-standard">GHG Protocol</a> and <a target="_blank" href="https://ghgprotocol.org/sites/default/files/ghgp/Global-Warming-Potential-Values%20%28Feb%2016%202016%29_1.pdf">Global Warming Potential values</a>.</p>`,
    how_it_works: 'How it works',
    bank_accounts_title: 'Bank accounts',
    bank_accounts_description:
      'Connect your bank accounts to automatically calculate the carbon emissions from each transaction.',
    fuel_heat_electricity_title: 'Fuel, heat and electricity',
    fuel_heat_electricity_description:
      'Enter your consumption of fuel, heating, and electricity consumption to complete your calculation.',
    security_text: `
      <p><b> Trusted data collection:</b> We partner with Plaid, a trusted banking service to gather your transaction details safely. We don’t store your bank login information, and we can’t make any transactions.</p>
      <p><b>Encryption:</b> We use top-notch methods to keep your data safe, encrypting it both when sending and storing.</p>
      <p><b>Privacy and control:</b> We collect only the necessary data we need to provide you with our services. You can always request to see, modify or delete your data from our system.</p>
      <p><b>Security Audits:</b> Our application has been designed with privacy in mind. To ensure the continued security of our system, we continuously inspect our security measures to be as strong as possible.</p>`,
    get_started: 'Get started',
  },
  IntegratorIntegrationList: {
    title: 'Integrator',
    description:
      'Choose from our library of integrators to extend Greenspark and add impact to many external apps',
  },
  ConnectedBankAccountList: {
    label: 'Connected accounts:',
    connect_account_title: 'Connect your bank accounts',
    more: 'See other connected accounts',
  },
  IntegrationMixin: {
    per_company_review: 'Per company review',
    per_product_review: 'Per product review',
    per_sms_subscriber: 'Per SMS subscriber',
    per_subscriber: 'Per subscriber',
    per_completed_form: 'Per completed form',
    per_selected_answer: 'Per selected answer',
    offset_by_percentage_order: 'Offset by percentage order',
    offset_by_spend: 'Spend threshold',
    offset_by_store_revenue: 'Tiered spend level',
    offset_per_order: 'Per order impact',
    offset_per_product: 'Per product (customised)',
    offset_all_products: 'Per product (all products)',
    per_paid_invoice: 'Per invoice',
    by_percentage_of_paid_invoice: '% of invoice value',
    by_spend_of_paid_invoice: 'Spend threshold',
    by_tiered_spend_of_paid_invoice: 'Tiered spend level',
    by_percentage_of_revenue: '% of total revenue',
    by_loyalty_points: 'Loyalty points',
    affiliate: 'Affiliate',
    review: 'Review',
    partner: 'Partner',
    subscriber: 'Subscriber',
    subscription: 'Subscription',
    transaction: 'Transaction',
    order: 'Order',
    zapier: 'Zapier',
    form_completion: 'Form Completion',
    other: 'Other',
    custom: 'Custom',
    loyalty: 'Loyalty',
  },
  NavigateBack: {
    back: 'Back',
  },
  AutomationsTable: {
    delete_automation_dialog_title: 'Delete automation',
    delete_automation_description:
      'By deleting your <span>{automation}</span> automation it will be lost forever and any associated processes need to be updated.',
    delete_automation: 'Delete automation',
    delete_automation_checkbox_label: 'I want to delete this automation',
    more: 'More',
    custom: 'Custom',
  },
  SelectExistingIntegration: {
    add_integration_title: 'Create additional integrations',
    add_integration_description: 'Add another integration. Any questions?',
    get_in_touch: 'Get in touch',
    add_integration: 'Add integration',
  },
  AddIntegrationAutomationView: {
    add_integration_title: 'Add integration',
    add_integration_description:
      'Now it’s time to start adding your impact. Start by creating your first integration.',
    select_integration_title: 'Select integration',
    select_integration_description: 'Select an existing integration or easily add a new one.',
    custom_integration_title: 'Great! Let’s setup your {source} integration',
    api_integration_description:
      'With our public API you can connect Greenspark to any other app or fully embed the experience in your product. Learn more by reading our  <a href="https://greenspark.readme.io" target="_blank">API documentation.</a>',
    create_api_key_title_custom: 'Create your API key',
    create_api_key_description:
      'Now that you have a source and trigger to identify your API calls, let’s create an API key that you can use to access with the Greenspark API.  Learn more with our <a href="https://greenspark.readme.io" target="_blank">API documentation.</a>',
    api_key_custom: 'Your API Key is ready!',
    custom_integration_description:
      'With our {source} integration API you can connect Greenspark to 5000+ apps. Learn more by reading our  <a href="{documentationUrl}" target="_blank">{source} integration documentation.</a>',
    create_custom_integration_key_description:
      'Create your {source} API key in Greenspark and use it to authenticate your {source} integration. Learn more: <a href="{documentationUrl}" target="_blank">{source} documentation.</a>',
    copy_custom_integration_key_description:
      'Copy your {source} API key into your {source} account to authenticate your integration. Learn more: <a href="{documentationUrl}" target="_blank">{source} documentation.</a>',
    create_api_key_title: 'Create your {source} key',
    copy_api_key_title: 'Copy your {source} key',
    create_zapier_key_description:
      'Now that you have a source and trigger to identify your Zapier calls, let’s create a Zapier key to authenticate your calls',
    api_key_zapier: 'Your Zapier Key is ready!',
    add_automation_title: 'Add automation',
    add_automation_description:
      'Select a trigger to automate your impact strategy by choosing which customer activity creates positive climate action.',
    choose_impact_title: 'Choose impact',
    choose_impact_description:
      'Automatically support your chosen projects to rescue plastic bound for our planet’s oceans, plant trees or offset CO2.',
    source_created: 'Source successfully created!',
    edit_integration_title: 'Edit your {source} integration',
    edit_integration_description: 'Edit your {source} integration in Greenspark.',
  },
  AutomationsView: {
    edit_popup_title: 'Edit automation',
    edit_automation_description: 'Easily manage and edit all your automation settings here.',
    edit_integration_title: 'Edit your {source} integration',
    edit_integration_description: 'Edit your {source} integration in Greenspark.',
    no_automations_text: 'You do not have any automations yet. Add one to get started.',
    add_automation: 'Add automation',
  },
  EditAutomation: {
    integration: 'Integration',
    automation: 'Automation',
    schedule: 'Schedule',
    schedule_tooltip:
      'Start your trigger continuously from today or choose a specific date range during which it will be active.',
    all_product: 'Set impact for all products',
    per_subscriber: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Impact per email subscriber',
      text: '{0} per e-mail subscriber',
      total: 'Total cost per subscriber: {price}',
    },
    per_sms_subscriber: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Impact per SMS subscriber',
      text: '{0} per SMS subscriber',
      total: 'Total cost per subscriber: {price}',
    },
    per_product_review: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Impact per product review',
      text: '{0} per product review',
      total: 'Total cost per product review: {price}',
    },
    per_company_review: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Impact per company review',
      text: '{0} per company review',
      total: 'Total cost per company review: {price}',
    },
    per_completed_form: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Per completed form',
      text: '{0} per completed form',
      total: 'Total cost per completed form: {price}',
      all: 'All',
    },
    per_selected_answer: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Per selected answer',
      text: '{0} per selected answer',
      total: 'Total cost per question: {price}',
      total_paid_plan: 'Total cost per selected answer if you subscribe to a paid plan: {price}',
      all: 'All',
    },
    offset_per_order: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Per order impact',
      text: '{0} per order',
      total: 'Total cost per order: {price}',
    },
    offset_per_product: {
      choose_impact: 'Choose your impact per product sold',
      choose_impact_tooltip:
        'Use the filters below to choose specific products and customise their individual impact. Tailor your setup for single or groups of products.',
      title: 'Per product (customised)',
      impact_per_product: 'Impact per product',
      text: '{0} per product',
      total: 'Total cost per product: {price}',
    },
    by_loyalty_points: {
      choose_impact: 'Set up impact per rule',
      choose_impact_tooltip:
        'Based on what you’ve set up on Stamped, select which impact you want to generate for this rule ID via Greenspark.',
      title: 'Loyalty points',
      text: '{0} per redemption',
      trees: '{count} tree planted | {count} trees planted',
      kelp: '{count} kelp planted | {count} kelp planted',
      carbon: '{count} kg of CO2 | {count} kgs of CO2',
      plastic: '{count} plastic bottle rescued | {count} plastic bottles rescued',
      water: '{count} day of water supplied | {count} days of water supplied',
      bees: '{count} x 20 bees protected | {count} x 20 bees protected',
      total: 'Total cost per redemption: {price}',
    },
    offset_all_products: {
      choose_impact: 'Choose your impact for all your products',
      choose_impact_tooltip:
        'Choose which impact projects you want to support for each product sold. This setting will be applied to all your products at once.',
      title: 'Per product (all products)',
      text: '{0} per product',
      total: 'Total cost per product: {price}',
    },
    offset_by_spend: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Spend threshold',
      text: '{0} per {1} order',
      total: 'Total cost per {spend_level} order: {price}',
      label: 'Basket / order value',
      tooltip:
        'Select the currency and total amount a customer should spend in your store in order to activate the impact trigger.',
    },
    offset_by_store_revenue: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact projects you want to support. You will be billed by the amount of impact units you select.',
      title: 'Tiered spend level',
      text: '{0} per {1} spent in your store',
      label: 'Spend threshold',
      tooltip:
        'Select the currency and spend threshold for a customer to activate the impact trigger (e.g. 3x spend = 3x impact).',
      total: 'Total cost per {spend_level} spent in your store: {price}',
    },
    offset_by_percentage_order: {
      choose_impact: 'Choose your impact',
      choose_impact_tooltip:
        'Choose which impact project you want to support. You will be billed by the amount of impact units you select.',
      title: '% of order impact',
      text: '{1} of the order will go to {0}',
      label: 'Order percentage',
      tooltip:
        'Enter the percentage of the order value you want to automatically dedicate to your chosen impact.',
    },
    per_paid_invoice: {
      title: 'Per invoice',
      text: '{amount} per paid invoice',
      choose_impact: 'Choose your impact per invoice paid',
      choose_impact_tooltip:
        'Choose which impact projects you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      total: 'Total cost per invoice: {price}',
    },
    by_percentage_of_paid_invoice: {
      title: '% of invoice value',
      text: '{type} of the invoice value will go to {amount}',
      choose_impact: 'Select your impact per percentage of invoice value',
      choose_impact_tooltip:
        'Choose which impact project you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      label: 'Invoice percentage',
      tooltip:
        'Enter the percentage of the invoice value you want to automatically dedicate to your chosen impact.',
    },
    by_spend_of_paid_invoice: {
      title: 'Spend threshold',
      text: '{amount} per {type} spent',
      choose_impact: 'Choose your impact per spend threshold',
      choose_impact_tooltip:
        'Choose which impact projects you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      label: 'Invoice value',
      tooltip: 'Select the invoice currency and total needed to trigger the automation.',
      total: 'Total cost per {spend_level} spent: {price}',
    },
    by_tiered_spend_of_paid_invoice: {
      title: 'Tiered spend level',
      text: '{amount} per {type} spent',
      choose_impact: 'Choose your impact per tiered spend level',
      choose_impact_tooltip:
        'Choose which impact projects you want to support for each paid invoice. You will be billed by the amount of impact units you select.',
      label: 'Tiered spend level',
      tooltip:
        'Select the currency and tiered spend level for a customer to activate the impact trigger (e.g. 3x spend = 3x impact).',
      total: 'Total cost per {spend_level} spent: {price}',
    },
    by_percentage_of_revenue: {
      title: '% of total revenue',
      text: '{type} of the invoice value will go to {amount}',
      choose_impact: 'Select your impact per percentage of total revenue',
      choose_impact_tooltip: 'Choose which impact projects you want to support.',
      label: 'Percentage of revenue',
      tooltip:
        'Enter the percentage of your total revenue per month that you want to automatically dedicate to your chosen impact.',
      total: '',
    },
    save: 'Save',
    more: 'More',
    min_limit: 'minimum {limit}',
    name_automation: 'Name your automation',
    automation_name: 'Automation name',
    custom_trigger_name_tooltip:
      'A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Don’t worry, you can always add more triggers later.',
    automation_tooltip:
      'Pick a clear and memorable name for your automation - your customers will not see it and you can change anytime.',
    transaction: 'Transaction',
    subscription: 'Subscription',
    order: 'Order',
    subscriber: 'Subscriber',
    review: 'Review',
    affiliate: 'Affiliate',
    partner: 'Partner',
    form_completion: 'Form completion',
    other: 'Other',
    source_label: 'Name your integration source',
    source_help:
      'First let’s give your new integration a name. This is only used internally and will never be seen by your customers. ',
    source_name: 'Source name',
    source_tooltip:
      'A Source is the first level to differentiate your created Impacts. It holds the name and id for the Custom Integration. For example: you can store the connected application name as a source name.',
    trigger_label: 'Name your integration Trigger',
    trigger_help_custom:
      'Use triggers to easily manage all of your API calls and ensure they are classified correctly in your analytics. ',
    trigger_help_zapier:
      'Use triggers to easily manage all of your different Zapier integrations and ensure they are classified correctly in your analytics. ',
    trigger_name: 'Trigger name',
    trigger_tooltip:
      'A Trigger is the second level to differentiate your created Impacts. It holds a name and a type. Don’t worry, you can always add more triggers later.',
    trigger_type_label: 'Edit your trigger type',
    trigger_type_help:
      'Use the trigger type to differentiate your calls, for example, you might want to have one for transactions and one for subscriptions. ',
    trigger_type: 'Trigger type',
  },
  EmailIntegrationAlertView: {
    title: 'Sync Greenspark with your email marketing platform!',
    description:
      'Enrich subscriber data in your email marketing platform with your Greenspark impact. Manage communications directly, enhance the customer experience and engage your community in your sustainability story across new touchpoints!',
    select_integration: 'Choose your email integration:',
    skip: 'Skip',
    dismiss: 'Do not show this again.',
  },
  EmailDataSyncView: {
    title: 'Sync data with {emailProvider}',
    description:
      'By integrating your email marketing platform you can enrich your audience data with existing impact actions and impact wallet links.',
    sync_toggle: {
      title: 'Sync data from all automations.',
      description:
        'Manage all of your customer impact directly within {emailProvider}. Use the data to create flows, segmentation and more.',
      disclaimer: 'If sync data is active, we don’t send out emails via Greenspark.',
    },
    sync_list: {
      title: 'By selecting data sync, the following data will be pushed if available:',
      profile: 'Profile',
      event: 'Event',
      contact: 'Contact',
      metric: 'Metric',
      documentation_link:
        'Check out our documentation page to see more details and variable examples',
      fields: {
        customer_impact: 'Customer impact',
        customer_impact_per_automation: 'Customer impact per automation',
        impact_certificate: 'Greenspark certificate',
        customer_total_impact: 'Overall impact of your customer',
        company_total_impact: 'Overall impact of your business',
        greenspark_customer_total_impact: 'Overall impact of your customer',
        integration_id: 'Integration Id',
        automation_ids: 'Automation Id',
        login_link: 'Customer dashboard login link',
        signup_link: 'Customer dashboard signup link',
        type: 'Type',
        impactsTextPresent: 'Present impacts',
        impactsTextPast: 'Past impacts',
        email: 'Email address',
        first_name: 'First name',
        firstName: 'First name',
        last_name: 'Last name',
        lastName: 'Last name',
        name: 'Name',
        greenspark_latest_impact_certificate: 'Greenspark certificate',
        greenspark_latest_login_link: 'Customer dashboard login link',
        greenspark_latest_signup_link: 'Customer dashboard signup link',
        greenspark_latest_customer_impact: 'Customer impact',
        impact_created_at: 'Impact created at',
        greenspark_customer_total_impact_trees: 'Overall tree impact of your customer',
        greenspark_customer_total_impact_plastic: 'Overall plastic impact of your customer',
        greenspark_customer_total_impact_carbon: 'Overall carbon impact of your customer',
        greenspark_customer_total_impact_water: 'Overall water impact of your customer',
        greenspark_customer_total_impact_kelp: 'Overall kepl impact of your customer',
      },
    },
    historical_data: {
      title: 'Sync historical data',
      description:
        'You can enrich your {emailProvider} audience data with existing impact actions and impact wallet links by running the historical data sync. Depending on your list size, this can take up to 24hrs to complete.',
      confirm:
        'I understand and confirm that I want to sync my historical data. Once you select this option, this action can’t be undone.',
      sync: 'Sync all data',
      date: 'Historical data synced on: {date}',
      in_progress:
        'Sync data have been started. As this may take a while, please come back later to check the results.',
      success:
        'Successfully initiated historical data sync, this can take up to 24hrs to complete.',
      impact_actions: 'Synced impact actions: {count}',
      error: {
        general: 'Error occured while initiating history sync, please try again later.',
        INTEGRATION_WRITE_DIRECTION_NOT_ENABLED:
          'To activate this, you need to activate “Sync data from all automations” at the top of this page.',
        INTEGRATION_WRITE_DIRECTION_SYNC_ONGOING:
          'Historical sync operation is already in progress.',
      },
    },
    test_connection: {
      title: 'Test your connection',
      description:
        'Send a test event to {emailProvider} and ensure that everything is set up correctly.',
      event: {
        disclaimer:
          'If the event creation is successful then you will see all the data available listed above in your {emailProvider}',
        profileEmail: 'A new audience profile with email: {value}',
        profileName: 'A new audience profile called: {value}',
        contactEmail: 'A new audience profile with email: {value}',
        contactName: 'A new audience profile called: {value}',
        eventName: 'An event for the audience profile called: {value}',
        metricName: 'An event for the audience profile called: {value}',
      },
      status: {
        title: 'Status:',
        default: 'Test my connection',
        loading: 'Testing',
        success: 'Connection successful.',
        error: 'Connection failed.',
      },
      date: 'Last tested on: {date}',
      success: 'Connection test successful! Check your {emailProvider} account.',
      error: {
        update_key:
          'The API key provided doesn’t have read and write permissions. Please update it by clicking here.',
        INTEGRATION_WRITE_DIRECTION_TEST_ERROR:
          'An error occured while syncing your email data, please try again.',
        INTEGRATION_WRITE_DIRECTION_TEST_FORBIDDEN:
          'The API key provided doesn’t have read and write permissions.',
      },
    },
  },
  ShopifySetupView: {
    header: 'Thanks for connecting your Shopify store!',
    description:
      "For uninterrupted use of Greenspark, we kindly ask you to accept the billing setup within Shopify for every Shopify integration. This step, compliant with Shopify's policy, is essential for the functioning of our application and allows us to charge your store if necessary. Although this is a Shopify requirement, rest assured that we will only apply the actual charges to a single store, ensuring fairness in our billing approach.",
    continue: 'Continue to Shopify',
    cancel: 'Cancel Integration',
  },
  ShopifySetupSuccessView: {
    header: 'Thanks for connecting your Shopify store!',
    description: 'We are just setting up the last details so you can create positive impact!',
  },
  ShopifyCustomSpendingLimitView: {
    step_one_title: 'Set custom spending limit for {store}',
    step_one_description:
      'Set a monthly spending limit to complete your payment setup in Shopify. <strong>You will be billed in $USD</strong>, and you can adjust your limit anytime in your dashboard under account.',
    step_two_title: 'Confirmation needed',
    step_two_description:
      'To confirm your spending limit for {store}, <strong>we’ll redirect you to Shopify to review and approve</strong> the updated charges.<br /><br />Once approved, you’ll return here, and your <strong>new spending limit will be active!</strong>',
    step_two_success_title: 'Your new limit is up and running!',
    step_two_success_description:
      'Your new spending limit of <strong>$ {amount} US Dollars</strong> has been set successfully.<br /><br />Thank you!',
    recommended: 'Recommended',
    enhanced: 'Enhanced',
    custom: 'Set your own',
    spending_limit_title: 'Monthly amount',
    spending_limit_description:
      'Set your maximum spending limit. Choose a preset amount or set your own below.',
    monthly_limit: 'Set monthly limit',
    min_spending_limit:
      'Your spending limit must be at least $ {min} US Dollars. This is to take into account your current monthly spend of $ {amount} US Dollars and your {plan} Plan.',
    error: 'An error occurred, please try again or get in touch and we would be happy to help.',
    add_impact_limit_reached_error:
      'This purchase will exceed your spending limit. Increase your spending limit to complete the purchase.',
    add_impact_approve_needed_error:
      'You must approve your spending limit before making any purchase. Follow the the instruction in the top banner.',
    tooltip: {
      recommended:
        'The recommended limit is calculated based on average monthly usage for similar business on your current plan.',
      enhanced:
        'Enhanced limits cover months that have campaigns with high positive impact creation, such as Black Friday, Earth Day and Christmas.',
    },
    proceed_to_shopify: 'Proceed to Shopify',
    cancel_shopify_integration: 'Cancel Shopify integration',
    new_spending_limit: 'New spending limit - Monthly:',
    my_account: 'Go to my account',
    limit_hit_warning_title: 'What happens if I hit the limit?',
    limit_hit_warning_description:
      'When your limit is reached, Greenspark services will be <strong>suspended</strong> until your budget is increased or the next billing cycle begins to prevent extra charges. All widgets as well as any active impact creation will be paused. You can <a href="https://help.getgreenspark.com/en/article/how-to-set-up-a-custom-spend-limit-53k2k3/" target="_blank">learn more about spending limit here.</a>',
    cancel_integration_alert_title: 'Are you sure you want to cancel this integration?',
    cancel_integration_alert_stripe_description:
      'If you cancel you will <strong>not complete your integration</strong> with Shopify and <strong>will revert to billing through your current method, Stripe</strong>. You will need to re-start the process in order to integrate Shopify in the future.',
    cancel_integration_alert_shopify_description:
      'If you cancel you <strong>integration will be deleted</strong> with Shopify and <strong>your subscription will be canceled</strong>. You will need to re-start the process in order to integrate Shopify in the future.',
    continue_integration: 'Continue integration',
    cancel_integration: 'Cancel and delete integration',
    current_spending_limit: 'Your current spending limit:',
    selected_spending_limit: 'New spending limit:',
    update_other_stores_limit_title:
      'You have other stores with spending limits that are below the minimum amount and need updating.',
    update_other_stores_limit_description:
      'This can be done once you head back to your main account page.',
  },
  SpendingLimitChart: {
    title: 'Manage your spending limit',
    description:
      'Customise your monthly budget to match your financial goals and sustainability efforts. Set a maximum spending limit for your contributions towards creating positive impact.',
    billing_cycle: 'Current amount spent this billing cycle:',
    limit_hit_email:
      'We’ll email you when you reach 80%, 95%, and 100% of your spending limit so you can stay on top of your spending.',
    approve_payment:
      '<strong>Action required:</strong> Approve payment in Shopify for this store. <a>Click here to approve.</a>',
    billing_blocked:
      '<strong>Action required:</strong> Your billing is blocked. <a>Click here to add a valid payment method.</a>',
    update_limit:
      '<strong>Action required:</strong> Update spending limit. <a>Click here to update.</a>',
  },
  UtilsMixin: {
    per_subscriber: {
      title: 'Impact per email subscriber',
      text: '{amount} per e-mail subscriber',
      total: 'Total cost per subscriber: {price}',
    },
    per_sms_subscriber: {
      title: 'Impact per SMS subscriber',
      text: '{amount} per SMS subscriber',
      total: 'Total cost per subscriber: {price}',
    },
    per_product_review: {
      title: 'Impact per product review',
      text: '{amount} per product review',
      total: 'Total cost per product review: {price}',
    },
    per_company_review: {
      title: 'Impact per company review',
      text: '{amount} per company review',
      total: 'Total cost per company review: {price}',
    },
    offset_per_order: {
      title: 'Per order impact',
      text: '{amount} per order',
      total: 'Total cost per order: {price}',
    },
    offset_per_product: {
      title: 'Per product impact',
      text: '{amount} per product',
      total: 'Total cost per product: {price}',
    },
    by_loyalty_points: {
      title: 'Loyalty points',
      text: '{amount} per redemption',
      trees: '{count} tree | {count} trees',
      kelp: '{count} kelp | {count} kelp',
      carbon: '{count} kg of CO2 | {count} kgs of CO2',
      plastic: '{count} plastic bottle | {count} plastic bottles',
      water: '{count} day of water | {count} days of water',
      bees: '{count} x 20 bees | {count} x 20 bees',
      total: 'Total cost per redemption: {price}',
    },
    offset_by_spend: {
      title: 'Spend threshold',
      text: '{amount} per {type} order',
      total: 'Total cost per {spend_level} order: {price}',
      label: 'Basket / order value',
      tooltip:
        'Select the currency and total amount a customer should spend in your store in order to activate the impact trigger.',
    },
    offset_by_store_revenue: {
      title: 'Tiered spend level',
      text: '{amount} per {type} spent in your store',
      label: 'Spend threshold',
      tooltip:
        'Select the currency and spend threshold for a customer to activate the impact trigger (e.g. 3x spend = 3x impact).',
      total: 'Total cost per {spend_level} spent in your store: {price}',
    },
    offset_by_percentage_order: {
      title: '% of order impact',
      text: '{type} of the order will go to {amount}',
      label: 'Order percentage',
      tooltip:
        'Enter the percentage of the order value you want to automatically dedicate to your chosen impact.',
    },
    per_completed_form: {
      title: 'Per completed form',
      text: '{amount} per completed form',
      total: 'Total cost per completed form: {price}',
    },
    per_paid_invoice: {
      title: 'Per invoice',
      text: '{amount} per paid invoice',
      total: 'Total cost per invoice: {price}',
    },
    by_percentage_of_paid_invoice: {
      title: '% of invoice value',
      text: '{type} of the invoice value will go to {amount}',
      label: 'Invoice percentage',
      tooltip:
        'Enter the percentage of the invoice value you want to automatically dedicate to your chosen impact.',
    },
    by_percentage_of_revenue: {
      title: '% of total revenue',
      text: '{type} of the total revenue value will go to {amount}',
      label: 'Invoice percentage',
      tooltip:
        'Enter the percentage of the total revenue you want to automatically dedicate to your chosen impact.',
    },
    by_spend_of_paid_invoice: {
      title: 'Spend threshold',
      text: '{amount} per {type} spent',
      label: 'Invoice value',
      tooltip: 'Select the invoice currency and total needed to trigger the automation.',
      total: 'Total cost per {spend_level} spent: {price}',
    },
    by_tiered_spend_of_paid_invoice: {
      title: 'Tiered spend level',
      text: '{amount} per {type} spent',
      label: 'Tiered spend level',
      tooltip:
        'Select the currency and tiered spend level for a customer to activate the impact trigger (e.g. 3x spend = 3x impact).',
      total: 'Total cost per {spend_level} spent: {price}',
    },
  },
  FreePlanCard: {
    header_title: 'Pay-as-you-Grow',
    header_desc:
      'Just starting out? Try our free plan for early-stage brands. A good way to add positive impact your customers will love from day one. Higher cost per impact but with zero subscription fees.',
    start: 'Start free',
    see_details: 'See plan details',
    current: 'Current plan',
    features_new: {
      0: 'Create impact with <b>vetted climate projects</b>',
      1: '<b>1 active user</b>',
      2: '<b>1 active app integration</b> with basic automations',
      3: 'Switch <b>impact</b> at any time',
      4: 'Dashboard access <b>to track and manage</b> your impact',
      5: 'Public profile, dynamic widgets and badges to <b>showcase and share your impact</b> to customers',
      6: "<b>Offset</b> your team's carbon and plastic footprints",
      7: '<b>Basic analytics</b> to track your impact',
      8: '<b>Impact pricing</b> increased to <b>{listOfPrices}</b>',
    },
  },
  EnterprisePlanCard: {
    header_title: 'Enterprise',
    header_desc:
      'Empower your enterprise for meaningful change with limitless possibilities and dedicated support.',
    start: 'Request a demo',
    see_details: 'See plan details',
    features_new: {
      0: '<strong>Unlimited user accounts</strong> for extensive team collaboration',
      1: '<strong>Unlimited</strong> API calls',
      2: '<strong>Dedicated customer success manager</strong> for personalised support',
      3: '<strong>Whitelabel options</strong> to customise the platform with your brand',
      4: '<strong>Personalised project sourcing</strong> for tailored climate projects',
      5: 'Custom SLA (Service Level Agreement) for <strong>guaranteed service standards</strong> ',
    },
  },
  PriceComparisonDialog: {
    title: 'You can save at least 25% on impact if you subscribe to one of our paid plans!',
    desc: 'This is the impact cost difference between your current plan and our paid plans:',
    current_plan: 'You’re paying',
    new_plan: 'Paid Plans',
    sum_current: 'So far, you’ve spent:',
    sum_new: 'How much you would’ve paid if you were subscribed to a paid plan:',
    change_plan: 'Change plan',
  },
  ProductTable: {
    not_found:
      '<b>No items match your search.</b><br />Check your spelling and filter options, or search for a different keyword.',
    add_impact: 'Add impact',
    add_bulk_impact: 'Add bulk impact',
    choose_impact: 'Choose impact amount for the product selected',
    choose_bulk_impact: 'Choose impact amount for {count} products selected',
    set_impact: 'Set impact',
    save_impact: 'Save impact',
    clear_impact: {
      label: 'Clear impact',
      title: 'Are you sure you want to clear impact for these products?',
      description:
        'If you clear the impact, these products will not create positive impact anymore.',
    },
    search: 'Search a product by name or type',
    total: 'Total impact cost per product sold: {price}',
    text: '{amount} per product',
    filter_by_impact: 'Filter by impact',
    all_projects: 'All Projects',
    sync_products: {
      title: 'Not seeing your product in the list?',
      button: 'Resync your products',
    },
  },
  RuleTable: {
    total: 'Total impact cost: {price}',
    save: 'Save Rule',
    title: 'Set up impact per rule',
    description:
      'Select which impact you want to generate for this Stamped Rule ID via Greenspark.',
    add_new: 'Add new rule',
    fields: {
      ruleId: 'Paste your Rule ID here',
      ruleName: 'Rule name',
    },
    headers: {
      ruleId: 'Rule ID',
      ruleName: 'Rule Name',
      impact: 'Impact',
      cost: 'Cost',
    },
    no_data: 'You currently don’t have any rules. Add your first one now!',
    delete_confirm: {
      title: 'Are you sure you want to delete this rule?',
      description: 'Once your rule is deleted, the impact tied to it will no longer be created.',
      checkbox: 'I want to delete this rule.',
    },
    where_to_find_rule_id: 'How to find my RuleID?',
    existing_rule_validation:
      'The provided ruleId already exists for this automation, please add a different one.',
  },
  LoyaltyPointsInfoPopup: {
    title: 'Loyalty Points Set Up',
    description:
      'In order to use this trigger, you need to set up a “free product” rule redemption in your Stamped account first.',
    first_step: `<p><strong>Set up a new rule</strong> in your Stamped account so customers can “Redeem Points” for a "Free Product" consisting of the impact of your choice.</p><p><strong>Set the impact you want to generate</strong> and <strong>how many points are needed</strong> to redeem it.</p><p><strong>Not sure how to do this?</strong><br /><a href="${STAMPED_LOYALTY_DOCUMENTATION_URL}" target="_blank">Check our step-by-step guide.</a></p>`,
    second_step:
      '<p><strong>Copy your Rule ID</strong> and <strong>click “Next”</strong> to set up your impact creation on Greenspark.</p>',
  },
  SelectFormImpactSource: {
    title: 'Impact source',
    all_forms: 'All forms',
    choose_one_form: 'Choose one form',
    link_text: 'Where can I find my Form ID?',
    all: 'All forms',
    choose_one: 'Choose one form',
    form_id: 'Form ID',
    where_to_find_form_id: 'Where can I find my Form ID?',
  },
  InviteUserOnboard: {
    email: 'Email',
    invite_user_button_text: 'Send invitation | Send invitations',
    invitation_sent: 'Invitation sent!',
    limit_reached:
      'You’ve reached the limit. But don’t worry, you can add more in your dashboard later.',
  },
  ImpactSettingsPerQuestion: {
    question: 'Question',
    answer: 'Answer',
    cost_per_answer: 'Cost per answer',
    new_answer: 'New answer',
    update_answer: 'Update answer',
    save_question: 'Save question',
    title: 'Per completed form',
    text: '{amount} per answer',
    total: 'Total cost per answer: {price}',
    impact: 'Impact',
    cost: 'Cost',
    cancel: 'Cancel',
    minimum: 'minimum {limit}{currency}',
  },
  PerSelectedAnswerPostPurchaseEmailNotification: {
    post_purchase_email_notification_text:
      'Post-purchase emails require a collected email address in the form.',
  },
  TopStats: {
    title: 'Your Impact',
    description: 'This is the impact generated by your company so far!',
    add_more_impact: 'Add More Impact',
  },
  TopStatsCard: {
    transfarred_impact: 'Transferred Impact:',
    partner_impact: 'Partner Impact:',
    months_earth_positive: 'earth positive',
    months_amount:
      '{count} <span class="smaller">month</span> | {count} <span class="smaller">months</span>',
  },
  PublicMap: {
    title: 'Projects your company is supporting',
    title_alternative: 'Projects your company can support',
    about_our_projects: 'Read more about our projects',
  },
  RebuyIntegrationPopup: {
    default_title: 'Rebuy integration',
    setup_title: 'How to set up your Rebuy integration',
    shopify_required_warning: 'Rebuy is a Shopify add-on.',
    shopify_required_feedback:
      'In order to use widgets in your Rebuy account, you’ll need to set up a Shopify integration first.',
    setup_shopify_cta: 'Set up Shopify integration',
    rebuy_disclaimer: 'Rebuy is a free integration because it’s a Shopify add-on.',
    in_order_to_use:
      'In order to use Greenspark widgets in your Rebuy account, you’ll need to set up your Rebuy Smart Cart. Here’s how you can do this:',
    setting_up: 'Setting up your Rebuy Smart Cart',
    setup_tutorial: {
      step_1: 'Begin by generating a Widget API key in your dashboard.',
      access_dashboard: 'Access dashboard',
      step_2_1: 'With your widget API key,',
      step_2_1_alt: 'Now, with your widget API key,',
      login_to_rebuy: 'log in to your Rebuy account',
      step_2_3: 'to get started',
      step_3: 'Follow the simple',
      installation_guide: 'Rebuy + Greenspark Smart Cart App Installation Guide',
      all_set: "You're all set!",
      step_4:
        'Choose your favourite widget from our library and add it to your Rebuy account by simply clicking “Use widget” and following the instructions.',
    },
    setup_complete: 'Setup complete, choose widdget',
  },
  DemoAlertBar: {
    title: 'This is a Demo account.',
    action: 'Subscribe to a full account here.',
  },

  carbon: {
    label: 'CO2',
    name: 'CO2',
    title: 'CO2',
    billed_action: 'CO2 offsetting',
    feature_label: 'CO2 offsetting',
    default_project_title: 'Improved Cook Stoves in Malawi',
    default_project_text:
      'Helping rural families switch to safer, cleaner, more efficient cookstoves. Switching to a Changu Changu Moto cookstove saves 2.5 tonnes of carbon a year.',
    community_label: 'Our community has <b>offset {amount} tonnes of CO2</b>',
    default_project_impact_description:
      'By helping rural families switch to more efficient cookstoves, you’re helping to reduce 2.5 tonnes of carbon emissions annually. This project has helped transition 40,000 three-stone cooking fires to energy-efficient stoves, benefiting 200,000 people in Malawi.',
    equivalent_to: 'Your CO2 offset is equivalent to:',
    action: 'Offset CO2',
    achievements_label: 'Carbon Champion',
    achievements_flavor: 'tonnes offset',
    achievements_level: '{current} / {next} {flavor}',
    achievements_level_max: 'Carbon Mastery Achieved!',
    simple_unit: 'Kg of Carbon',
    unit: 'Tonne of CO2 | Tonnes of CO2',
    amount_label: '{count} kg of CO2 | {count} kgs of CO2',
    offset_amount: 'offset {amount}kg of CO2 | offset {amount}kgs of CO2',
    ongoing_action: 'Offsetting carbon',
    action_descriptor: 'carbon offsetting',
    executed_action: 'tonne of CO2 offset | tonnes of CO2 offset',
    price_per_unit: '{price} per kg of CO2',
    short_price: '{currencySymbol}{price} / kg',
    transferred_impact_disclaimer:
      '{count} tonne of CO2 has been transferred from a previous source. Check the public ledger to see the proof. | {count} tonnes of CO2 have been transferred from a previous source. Check the public ledger to see the proof.',
    partner_impact_disclaimer:
      '{count} tonne of CO2 has come from partnerships. | {count} tonnes of CO2 have come from partnerships.',
    impact_action: 'Kg of CO2',
    impact_action_label:
      '{count} tonne of CO2 has come from impact actions. | {count} tonnes of CO2 have come from impact actions.',
  },
  plastic: {
    label: 'Plastic',
    action: 'Rescue plastic',
    billed_action: 'Ocean-bound plastic rescuing',
    title: 'Plastic',
    feature_label: 'Ocean-bound plastic rescuing',
    default_project_title: 'Transforming Plastic Waste Into Empowering Income',
    default_project_text:
      'Plastic Bank® builds ethical recycling ecosystems in coastal communities while simultaneously creating positive economic opportunities for those who helped collect it.',
    community_label: 'Our community has <b>rescued {amount} plastic bottles</b>',
    default_project_impact_description:
      'Today, over 5 trillion pieces of plastic litter the ocean. Your impact is helping build ethical recycling ecosystems in coastal communities while simultaneously creating positive economic opportunities for those who helped collect it.',
    equivalent_to: 'Your plastic is equivalent to:',
    achievements_label: 'Plastic Protector',
    achievements_flavor: 'plastic bottles',
    achievements_level: '{current} / {next} {flavor}',
    achievements_level_max: 'Head Plastic Honcho Achieved!',
    simple_unit: 'Plastic Bottle | Plastic Bottles',
    unit: 'Plastic bottle | Plastic bottles',
    amount_label: '{count} plastic bottle | {count} plastic bottles',
    offset_amount: 'rescue {amount} plastic bottle | rescue {amount} plastic bottles',
    action_descriptor: 'plastic bottle rescued',
    ongoing_action: 'Rescuing plastic',
    executed_action: 'plastic bottle rescued | plastic bottles rescued',
    price_per_unit: '{price} per plastic bottle',
    short_price: '{currencySymbol}{price} / bottle',
    transferred_impact_disclaimer:
      '{count} plastic bottle has been transferred from a previous source. Check the public ledger to see the proof. | {count} plastic bottles have been transferred from a previous source. Check the public ledger to see the proof.',
    partner_impact_disclaimer:
      '{count} plastic bottle has come from partnerships. | {count} plastic bottles have come from partnerships.',
    impact_action: 'Plastic bottle rescued | Plastic bottles rescued',
    impact_action_label:
      '{count} plastic bottle has come from impact actions. | {count} plastic bottles have come from impact actions.',
  },
  trees: {
    label: 'Trees',
    action: 'Plant trees',
    billed_action: 'Tree planting',
    title: 'Trees',
    feature_label: 'Tree planting',
    kenya_feature_label: 'Tree planting in Kenya',
    oregon_feature_label: 'Tree planting in Oregon',
    canada_feature_label: 'Tree planting in Canada',
    florida_feature_label: 'Tree planting in Florida',
    haiti_feature_label: 'Tree planting in Haiti',
    ontario_feature_label: 'Tree planting in Ontario',
    brazil_feature_label: 'Tree planting in Brazil',
    default_project_title: 'Fighting Extreme Poverty Through Tree Planting',
    default_project_text:
      'EarthLungs Mangrove Restoration Project is on a mission to combat deforestation by employing local communities to plant trees, providing an empowering means to alleviate extreme poverty through environmental stewardship.',
    community_label: 'Our community has <b>planted {amount} trees</b',
    default_project_impact_description:
      'Nearly 75% of the earth’s surface has been altered and impacted by human activity. Every tree you plant helps to employ local communities to plant trees, providing an empowering means to alleviate extreme poverty through environmental stewardship.',
    equivalent_to: 'Your tree planting is equivalent to:',
    achievements_label: 'Forest Guardian',
    achievements_flavor: 'trees planted',
    achievements_level: '{current} / {next} {flavor}',
    achievements_level_max: 'Tree Wizardry Achieved!',
    simple_unit: 'Tree | Trees',
    unit: 'Tree planted | Trees planted',
    amount_label: '{count} tree | {count} trees',
    amount_label_with_country: '{count} tree ({country})| {count} trees ({country})',
    offset_amount: 'plant {amount} tree | plant {amount} trees',
    action_descriptor: 'tree planting',
    executed_action: 'tree planted | trees planted',
    ongoing_action: 'Planting trees',
    price_per_unit: '{price} per tree',
    short_price: '{currencySymbol}{price} / tree',
    from_short_price: '<span class="small">from</span> {currencySymbol}{price} / tree',
    transferred_impact_disclaimer:
      '{count} tree has been transferred from a previous source. Check the public ledger to see the proof. | {count} trees have been transferred from a previous source. Check the public ledger to see the proof.',
    partner_impact_disclaimer:
      '{count} tree has come from partnerships. | {count} trees have come from partnerships.',
    impact_action: 'Tree planted | Trees planted',
    impact_action_label:
      '{count} tree has come from impact actions. | {count} trees have come from impact actions.',
  },
  kelp: {
    label: 'Kelp',
    action: 'Plant kelp',
    billed_action: 'Kelp planting',
    feature_label: 'Kelp planting',
    title: 'Seaforestation',
    default_project_title: 'Supporting Kelp restoration in British Columbia',
    default_project_text:
      'This project restores critical marine ecosystems and helps to increase biodiversity and mitigate the effects of climate change. The project is managed by Ocean Wise in collaboration with local indigenous communities and restores habitats for native species such as Herring, Salmon, and Northern Abalone.',
    community_label: 'Our community has <b>planted {amount} kelp</b>',
    default_project_impact_description:
      'This project restores critical marine ecosystems and helps to increase biodiversity and mitigate the effects of climate change. The project is managed by Ocean Wise in collaboration with local indigenous communities and restores habitats for native species such as Herring, Salmon, and Northern Abalone.',
    equivalent_to: 'Your kelp is equivalent to:',
    achievements_label: 'Kelp Custodian',
    achievements_flavor: 'kelp planted',
    achievements_level: '{current} / {next} {flavor}',
    achievements_level_max: 'Seaweed Stewardship Achieved!',
    simple_unit: 'Kelp',
    unit: 'Kelp planted',
    amount_label: '{count} kelp | {count} kelp',
    offset_amount: 'plant {amount} kelp',
    action_descriptor: 'kelp planting',
    executed_action: 'kelp planted',
    ongoing_action: 'Planting kelp',
    price_per_unit: '{price} per kelp',
    short_price: '{currencySymbol}{price} / kelp',
    transferred_impact_disclaimer:
      '{count} kelp has been transferred from a previous source. Check the public ledger to see the proof. | {count} kelp have been transferred from a previous source. Check the public ledger to see the proof.',
    partner_impact_disclaimer:
      '{count} kelp has come from partnerships. | {count} kelp have come from partnerships.',
    impact_action: 'Kelp planted | Kelp planted',
    impact_action_label:
      '{count} kelp has come from impact actions. | {count} kelp have come from impact actions.',
  },
  water: {
    label: 'Water',
    action: 'Clean water',
    title: 'Water',
    billed_action: 'Days of clean water',
    feature_label: 'Clean water provision',
    default_project_title: 'Transforming Lives Through Access To Clean Water',
    default_project_text:
      'The Water Project is a non-profit organisation dedicated to address the crisis around access to water worldwide by building sustainable water projects, giving communities access to clean and safe drinking water.',
    community_label: 'Our community has <b>provided {amount} days of clean water</b>',
    default_project_impact_description:
      'Providing clean water with The Water Project, a non-profit organisation dedicated to providing reliable water solutions to communities in sub-Saharan Africa. They build sustainable water projects to provide clean drinking water to communities worldwide.',
    equivalent_to: 'The water you supplied is equivalent to:',
    achievements_label: 'Water Warrior',
    achievements_flavor: 'days of water supplied',
    achievements_level: '{current} / {next} {flavor}',
    achievements_level_max: 'Hydration Hero Achieved!',
    simple_unit: 'Day of clean water | Days of clean water',
    unit: 'Day of water supplied | Days of water supplied',
    amount_label: '{count} day of water supplied | {count} days of water supplied',
    offset_amount: '{amount} day of water supplied | {amount} days of water supplied',
    action_descriptor: 'day of clean water',
    ongoing_action: 'Providing clean water',
    executed_action: 'day of water supplied | days of water supplied',
    price_per_unit: '{price} per day of clean water',
    short_price: '{currencySymbol}{price} / day',
    transferred_impact_disclaimer:
      '{count} day of clean water provided has been transferred from a previous source. Check the public ledger to see the proof. | {count} days of clean water provided have been transferred from a previous source. Check the public ledger to see the proof.',
    partner_impact_disclaimer:
      '{count} day of clean water provided has come from partnerships. | {count} days of clean water provided have come from partnerships.',
    impact_action: 'Day of water supplied | Days of water supplied',
    impact_action_label:
      '{count} day of clean water provided has come from impact actions. | {count} days of clean water provided have come from impact actions.',
  },
  bees: {
    label: 'Bees',
    action: 'Protect bees',
    title: 'Bees',
    billed_action: 'Bee protection',
    feature_label: 'Bee protection',
    default_project_title: 'Creating pollinator habitats and fostering biodiversity',
    default_project_text:
      'Bees are essential for life on Earth, pollinating 75% of the crops we eat and playing a vital role in sustaining ecosystems. By creating safe environments in Kenya for these tiny superheroes, we’re helping to restore the balance of nature, support local communities, and combat the biodiversity crisis.',
    community_label: 'Our community has <b>protected {amount} bees</b>',
    default_project_impact_description:
      'Bees are essential for life on Earth, pollinating 75% of the crops we eat and playing a vital role in sustaining ecosystems. By creating safe environments in Kenya for these tiny superheroes, we’re helping to restore the balance of nature and much more.',
    equivalent_to: 'The bees you protected are equivalent to:',
    achievements_label: 'Queen Bee',
    achievements_flavor: 'bees protected',
    achievements_level: '{current} / {next} {flavor}',
    achievements_level_max: 'Queen Bee Achieved!',
    simple_unit: '20 x Bees | 20 x Bees',
    unit: 'Bee protected | Bees protected',
    amount_label: '{count} x 20 bees protected | {count} x 20 bees protected',
    offset_amount: '{amount} x 20 bees protected | {amount} x 20 bees protected',
    action_descriptor: 'bees',
    ongoing_action: 'Protecting bees',
    executed_action: 'bee protected | bees protected',
    price_per_unit: '{price} per 20 bees protected',
    short_price: '{currencySymbol}{price} / 20 bees',
    transferred_impact_disclaimer:
      '{count} bee provided has been transferred from a previous source. Check the public ledger to see the proof. | {count} bees provided have been transferred from a previous source. Check the public ledger to see the proof.',
    partner_impact_disclaimer:
      '{count} bee provided has come from partnerships. | {count} bees provided have come from partnerships.',
    impact_action: 'Bee protected | Bees protected',
    impact_action_label:
      '{count} bee provided has come from impact actions. | {count} bees provided have come from impact actions.',
  },
  Projects: {
    trees: '{count} tree planted | {count} trees planted',
    kelp: '{count} kelp planted | {count} kelp planted',
    carbon: '{count} tonne of CO2 (Carbon Pool) | {count} tonnes of CO2 (Carbon Pool)',
    plastic: '{count} plastic bottle rescued | {count} plastic bottles rescued',
    water: '{count} day of water supplied | {count} days of water supplied',
    bees: '{count} bee protected | {count} bees protected',
    deprecated: 'Completed',
    // Eden Reforestation Projects
    '9be7083e-be8b-4608-96d0-a526445b4667': {
      impact: 'Plant trees in Honduras',
      title: 'Plant trees in Honduras',
      name: 'Eden Reforestation Projects',
      summary: 'Fighting Extreme Poverty Through Tree Planting',
      description:
        'Eden Reforestation Projects is on a mission to combat deforestation by employing local communities to plant trees, providing an empowering means to alleviate extreme poverty through environmental stewardship.',
      project: 'Plant trees in Honduras',
    },
    // Sunshine Coast kelp
    '9deda055-cd6d-4ebe-b343-4c9b73ccf2d6': {
      impact: 'Plant kelp',
      title: 'Kelp Forest Restoration in Canada',
      name: 'Ocean Wise',
      summary: 'Supporting Kelp restoration in British Columbia, Canada.',
      description:
        'This project restores critical marine ecosystems and helps to increase biodiversity and mitigate the effects of climate change. The project is managed by Ocean Wise in collaboration with local indigenous communities and restores habitats for native species such as Herring, Salmon, and Northern Abalone.',
      project: 'Kelp Forest Restoration in Canada',
    },
    // Carbon Pool project id
    '7accc260-ab82-4c51-a959-e6736284c45a': {
      impact: 'Offset carbon emissions',
      title: 'Improved Cook Stoves in Malawi',
      name: 'UN Climate Change',
      summary: 'Helping rural families switch to safer, cleaner, more efficient cookstoves.',
      description:
        'Support our portfolio of the best in class carbon projects around the world. Each is certified by Verra, the UNFCCC or Gold Standard to ensure it’s social and environmental benefits are maximised.',
      project: 'UN Climate Change',
    },
    // Plastic Bank project id
    '596f61ee-7149-43ac-ba4e-efb90ee3bd47': {
      impact: 'Rescue plastic',
      title: 'Transforming Plastic Waste Into Empowering Income',
      name: 'Plastic Bank',
      summary: 'Ethical recycling ecosystems that create positive economic opportunities.',
      description:
        'Remove plastic headed for our planet’s oceans. Your impact is building ethical recycling ecosystems and creating jobs for those who helped collect it.',
      project: 'Plastic Bank',
    },
    // Veritree EarthLung project id
    'c6e4b1bf-843e-4859-bc85-c3b764f507e6': {
      impact: 'Plant trees in Kenya',
      title: 'Uplifting Communities Through Mangrove Restoration',
      name: 'EarthLungs',
      summary: 'Restore critical mangrove ecosystems and promote economic opportunity in Kenya.',
      description:
        'EarthLungs is on a mission to restore critical mangrove trees that promote biodiversity growth and provide local communities with fair work and wages. Mangroves are a haven for birds, fish and other wildlife whilst also protecting coastlines from storms and erosion.',
      project: 'Plant trees in Kenya',
    },
    // Malawi Cook Stoves project id
    'ca724c03-6e8b-4c17-800d-80177ede64c7': {
      title: 'Improved Cook Stoves in Malawi',
      summary: 'Helping rural families switch to safer, cleaner, more efficient cookstoves.',
      description:
        'Improved Cookstoves fight deforestation and reduce greenhouse gas emissions; Switching to a Changu Changu Moto saves 2.5 tonnes of carbon a year.',
      project: 'Improved Cook Stoves in Malawi',
    },
    // Burgos Wind project id
    'ad2dd565-c711-48d5-bb43-85d8a41fd369': {
      title: 'Supporting Communities Through Wind Power',
      summary:
        'The UN-supported Burgos Wind project is helping to turn the tide on this negative environmental impact.',
      description:
        'The project is providing the Philippines with a source of clean, renewable energy and stable employment for local communities.',
      project: 'Supporting Communities Through Wind Power',
    },
    // Chile Hydroelectric Energy project id
    '59d6e52f-2fc1-4204-b441-f6a87d572dd4': {
      title: 'Offset CO2 and help develop Chile economically',
      summary:
        'The Chacayes Hydro Project provides enough clean energy to power over for 300,000 Chilean homes.',
      description:
        'In addition to providing clean energy, the Chacayes hydroelectric project provided key infrastructure including roads and access routes.',
      project: 'Offset CO2 and help develop Chile economically',
    },
    // Oregon trees
    'a0636f61-c9a4-4e42-ad34-26155f3dd827': {
      impact: 'Plant trees in Oregon',
      title: 'Plant trees in Oregon',
      name: 'American Forests - Oregon',
      summary:
        'Restoring areas affected by wildfires in Oregon by planting trees native to the Pacific Northwest.',
      description:
        'This project is managed by the Oregon Forestry Department with the aim of restoring habitats for species such as Deer, Elk, Cougar, Bear, Trout and Spotted Owl. The trees being planted are carefully selected to represent the local ecosystem and assist with natural regeneration.',
      project: 'Plant trees in Oregon',
    },
    // Florida trees
    '7b726bbc-b121-4432-96d8-30d507a19664': {
      impact: 'Plant trees in Florida',
      title: 'Plant trees in Florida',
      name: 'American Forests - Florida',
      summary: 'Revitalising Ecosystems Through Terrestrial Restoration',
      description:
        "The Lake Wales Ridge State Forest is a vital component of the Kissimmee Basin ecosystem. By planting trees and restoring the native longleaf pine ecosystem, American Forests is making the region more resilient to climate change, improving water quality, reducing runoff, and protecting the region's diverse wildlife.",
      project: 'Plant trees in Florida',
    },
    // Canada trees
    'edd27415-88b7-4747-b5cb-a8d9d3fa52be': {
      impact: 'Plant trees in Canada',
      title: 'Plant trees in Canada',
      name: 'Replant',
      summary: 'Rebuilding Habitats Through Terrestrial Planting',
      description:
        'Replant is committed to restoring areas previously damaged by commercial logging. By planting over 16 distinct native species, the project helps to reverse land degradation, revitalise habitats, increase biodiversity, and restore these forests as effective carbon sinks.',
      project: 'Plant trees in Canada',
    },
    // Haiti trees
    '6b563fc1-033b-40ea-89b7-c8702b74b5af': {
      impact: 'Plant trees in Haiti',
      title: 'Plant trees in Haiti',
      name: 'Mother Trees',
      summary: 'Supporting Pollinator Agroforestry in Haiti',
      description:
        'By planting native pollinator tree species, Mother Trees is restoring and protecting vital forests. The work not only benefits the ecosystem, it brings an empowering source of income to a region in severe need of economic support, enhancing livelihoods and strengthening local communities.',
      project: 'Plant trees in Haiti',
    },
    [ONTARIO_TREES_PROJECT_ID]: {
      impact: 'Plant trees in Ontario',
      title: 'Plant trees in Ontario',
      name: 'City of Sudbury',
      summary: 'Reviving Ecosystems through Terrestrial Reforestation',
      description:
        'This project revitalises Ontario’s degraded landscapes in Greater Sudbury through diverse terrestrial reforestation. By planting over 25 native tree species, it restores critical wildlife habitats, while sequestering carbon and boosting local employment. Advanced geospatial and IoT monitoring ensure sustainable, transparent progress in reviving this historic ecosystem.',
      project: 'Plant trees in Ontario',
    },
    [BRAZIL_TREES_PROJECT_ID]: {
      impact: 'Plant trees in Brazil',
      title: 'Plant trees in Brazil',
      name: 'SamaÚma',
      summary: 'Mangrove Restoration for Biodiversity',
      description:
        'This project revitalises Maranhão’s coastal ecosystems in Eastern Brazil through targeted mangrove restoration. By replanting native mangrove species, it shields the coastline, boosts carbon capture, and supports local communities. Advanced geospatial and drone monitoring ensure sustainable, transparent progress in restoring vital habitats and enhancing biodiversity.',
      project: 'Plant trees in Brazil',
    },
    // The Water Project
    '6b0add6d-246f-4a22-a072-3469eb65b225': {
      impact: 'Clean water',
      title: 'Clean water',
      name: 'The Water Project',
      summary: 'Transforming Lives Through Access To Clean Water',
      description:
        'The Water Project is a non-profit organisation dedicated to providing reliable water solutions to communities in sub-Saharan Africa. The project addresses the crisis around access to water worldwide by building sustainable water projects, giving  communities access to clean and safe drinking water.',
      project: 'The Water Project',
    },
    // Bees Project
    'bd5df270-6223-46e1-baf1-04d7e20f429f': {
      impact: 'Protect bees in Kenya',
      title: 'Protect bees in Kenya',
      name: 'EarthLungs',
      summary: 'Creating pollinator habitats and fostering biodiversity',
      description:
        'Bees are essential for life on Earth, pollinating 75% of the crops we eat and playing a vital role in sustaining ecosystems. By creating safe environments in Kenya for these tiny superheroes, we’re helping to restore the balance of nature, support local communities, and combat the biodiversity crisis.',
      project: 'Protect bees in Kenya',
    },
  },
  create_account_title: 'Create your Greenspark account',
  form_label_name: 'Your Name',
  form_label_company_name: 'Company Name',
  form_label_email: 'Email',
  form_email_alread_used: 'Email already used.',
  form_label_password: 'Password',
  form_hint_password_complexity:
    '*Your password must be at least 6 letters long, contain at least one special character and a mix of upper/lower case letters.',
  form_label_vat_number: 'VAT Number (EU only)',
  form_label_terms_and_conditions:
    'Accept our <a href="{termsAndConditionsUrl}" target="_blank">Terms & Conditions</a> & <a href="{privacyPolicyUrl}" target="_blank">Privacy Policy</a>',
  create_account_cta: 'Create account',
  get_started_in_steps_header: 'Get started with Greenspark in just a few simple steps:',
  create_your_account: 'Create your account',
  signup_fill_out_form:
    'Fill out the form with your basic information, including your email, password, and company details.',
  set_up_your_automations: 'Set up your automations',
  choose_how_to_make_impact:
    'Choose how you want to make a positive impact (like planting trees or rescuing plastic) with each product or order.',
  thats_it: "That's it!",
  account_is_ready:
    'Your account is ready to go. You can manage automations and track impact on your dashboard.',
  customer_engagement_increase_title: '64% Increase in',
  customer_engagement_increase_subtitle: 'Customer Engagement',
  conversion_rate_increase_title: '1.8x Increase in',
  conversion_rate_increase_subtitle: 'Conversion Rate',
  rise_in_order_value_title: '21% Rise in',
  rise_in_order_value_subtitle: 'Average Order Value',
  campaign_revenue_title: '2.7x Uplift in',
  campaign_revenue_subtitle: 'Campaign Revenue',
  billing_is_blocked: 'Your billing is blocked!',
  already_member: 'Already a member?',
  login: 'Login',
  days_to_add_payment_method_warning:
    'You have <strong>{count} day to add a valid payment method</strong> or your Greenspark subscription may be cancelled. | You have <strong>{count} days to add a valid payment method</strong> or your Greenspark subscription may be cancelled.',
  manage_payment_cta: 'Manage payment',
  close_to_hit_limit_title: 'You’re close to hitting your limit for {store}!',
  close_to_hit_limit_description:
    '<strong>You’re close to reach the limit for {store}.</strong> Positive impact creation, widgets, and media services will be paused once you hit the limit.',
  close_to_hit_limit_multiple_title: 'You’re close to hitting your limit for multiple stores!',
  close_to_hit_limit_multiple_description:
    '<strong>You’re close to reach the limit for multiple stores.</strong> Positive impact creation, widgets, and media services will be paused once you hit the limit.',

  limit_hit_title: 'You’ve hit your spending limit for {store}!',
  limit_hit_multiple_title: 'You’ve hit your spending limit for multiple stores!',
  limit_hit_description:
    '<strong>You’ve reached the limit for {store}.</strong> Positive impact creation, widgets, and media services are now paused. <strong>You must update your spending limit to reactivate these features.</strong>',
  limit_hit_multiple_description:
    '<strong>You’ve reached the limit for multiple stores.</strong> Positive impact creation, widgets, and media services are now paused. <strong>You must update your spending limit to reactivate these features.</strong>',
  update_limit: 'Update limit',
  approve_limit: 'Approve limit',
  approve_limit_title: 'You must approve your spending limit for {store}!',
  approve_limit_nudge_title: 'You have a pending spend limit of ${amount} for {store}!',
  approve_limit_description:
    'You didn’t finish setting Shopify as your payment method for {store}.',
  approve_limit_nudge_description:
    'Your current limit will stay in place until you approve the new limit.',
  setup_payment_header: 'Set up a payment method! 📣',
  youre_so_close: 'You’re so close to finalising your account setup!',
  add_valid_payment_tutorial_body:
    'All you need to do is <strong>add a valid payment method</strong> to your Greenspark account, and you’ll be good to go. Once active, <strong>you’ll pay for your Greenspark account directly through your payment provider.</strong>',
  no_messy_billing: 'No messy billing, no separate invoices. <strong>Easy!</strong>',
  follow_the_link: 'Just follow the link below to complete your setup.',
  setup_payment_note:
    'Note: Without this connection you won’t be able to fully use your account, and your subscription will be deactivated in {count} day. | Note: Without this connection you won’t be able to fully use your account, and your subscription will be deactivated in {count} days.',
  or: 'or',
}

export default en
